import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { BsPlusLg, BsPlayFill, BsPencil, BsTrash, BsArrowRepeat, BsRobot, BsTools, BsDiagram3, BsShieldCheck, BsListOl, BsDatabase, BsGear, BsCpu, BsSpeedometer2, BsCalendar2Check, BsClockHistory, BsLightbulb, BsJournalText, BsBriefcase, BsBinoculars, BsGlobe, BsEye, BsBoxArrowUpRight, BsGraphUpArrow, BsBarChartLine, BsEnvelope } from 'react-icons/bs';
import { apiFetch, parseJsonSafe, handleUnauthorized } from '../utils/api';

const WEEKDAYS = [
  { value: 1, label: 'Lunes' },
  { value: 2, label: 'Martes' },
  { value: 3, label: 'Miércoles' },
  { value: 4, label: 'Jueves' },
  { value: 5, label: 'Viernes' },
  { value: 6, label: 'Sábado' },
  { value: 7, label: 'Domingo' }
];

const emptyForm = {
  id: null,
  titulo: '',
  instrucciones: '',
  scheduleType: 'diaria',
  runAtTime: '09:00',
  weekday: 1,
  intervalMinutes: 60,
  enabled: true
};

const STATUS_META = {
  running: { label: 'En ejecución', className: 'bg-info text-dark' },
  ok: { label: 'OK', className: 'bg-success' },
  error: { label: 'Error', className: 'bg-danger' }
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || { label: status, className: 'bg-secondary' };
  return <span className={`badge ${meta.className}`}>{meta.label}</span>;
};

const formatDateTime = (value) => {
  if (!value) return '-';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleString('es-ES', { dateStyle: 'short', timeStyle: 'short' });
};

// El backend caduca el candado running_since a los 30 min; la UI aplica la misma ventana
// para que un candado huérfano (proceso reiniciado) no bloquee el botón indefinidamente.
const isTaskRunning = (task) => {
  if (!task.running_since) return false;
  const started = new Date(task.running_since).getTime();
  return Number.isFinite(started) && Date.now() - started < 30 * 60 * 1000;
};

const scheduleLabel = (task) => {
  if (task.schedule_type === 'diaria') return `Diaria a las ${task.run_at_time || '--:--'}`;
  if (task.schedule_type === 'semanal') {
    const day = WEEKDAYS.find((w) => w.value === Number(task.weekday));
    return `Semanal (${day ? day.label.toLowerCase() : '?'}) a las ${task.run_at_time || '--:--'}`;
  }
  if (task.schedule_type === 'intervalo') return `Cada ${task.interval_minutes} min`;
  return 'Manual';
};

const AgentesPage = () => {
  const token = localStorage.getItem('token');
  const authHeaders = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [tab, setTab] = useState('comercial');
  const [subtab, setSubtab] = useState('resumen');
  const [agents, setAgents] = useState([]);
  const [proveedores, setProveedores] = useState(null);
  const [claudeDisponible, setClaudeDisponible] = useState(false);
  const [tasks, setTasks] = useState([]);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [runningTaskId, setRunningTaskId] = useState(null);

  const [quickOrder, setQuickOrder] = useState('');
  const [quickRunning, setQuickRunning] = useState(false);

  const [logDetail, setLogDetail] = useState(null);
  const [capsOpen, setCapsOpen] = useState(false);

  // Configuración persistente del agente (indicaciones de tarifas, tipos de campaña)
  const [settings, setSettings] = useState({});
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsDraft, setSettingsDraft] = useState(null);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [quickCampaignTipo, setQuickCampaignTipo] = useState('');

  // Propuestas de captación (generadas por el agente, se pulen por chat y se aprueban)
  const [proposals, setProposals] = useState([]);
  // Descartar con motivo: el agente aprende de por qué se rechaza una propuesta
  const [discardModal, setDiscardModal] = useState(null); // { proposal, motivo, nota }
  const DISCARD_REASONS = [
    { key: 'sector', label: 'El sector no nos interesa (no volver a proponerlo)' },
    { key: 'ciudades', label: 'Las ciudades no encajan' },
    { key: 'enfoque', label: 'Enfoque poco comercial o poco creíble' },
    { key: 'repetida', label: 'Repite algo ya hecho o parecido' },
    { key: 'oferta', label: 'La oferta o el tipo de campaña no encaja' },
    { key: 'otro', label: 'Otro motivo' }
  ];
  const [proposalChat, setProposalChat] = useState({});      // id -> texto del input
  const [proposalBusy, setProposalBusy] = useState({});      // id -> true mientras ajusta/aprueba
  const [proposalsGenerating, setProposalsGenerating] = useState(false);
  const [expandedProposal, setExpandedProposal] = useState(null);

  // Memoria episódica del agente (notas por cliente/sector, editables)
  const [memoryNotes, setMemoryNotes] = useState([]);
  const [memoryFilter, setMemoryFilter] = useState('');
  const [memoryForm, setMemoryForm] = useState({ scope: 'cliente', clientQuery: '', clientId: null, sector: '', nota: '' });
  const [memoryClientOptions, setMemoryClientOptions] = useState([]);
  const [memorySaving, setMemorySaving] = useState(false);
  const [memoryEditing, setMemoryEditing] = useState(null); // {id, nota}

  // Panel de modelos de IA de toda la app
  const [modelsPanel, setModelsPanel] = useState(null); // {componentes, proveedores, claudeDisponible}
  const [modelsOpen, setModelsOpen] = useState(false);
  const [modelSaving, setModelSaving] = useState('');
  const [anthropicKeyInput, setAnthropicKeyInput] = useState('');
  const [anthropicSaving, setAnthropicSaving] = useState(false);

  const [showCompletadas, setShowCompletadas] = useState(false); // completadas plegadas por defecto

  // Landing de captación: previsualización (iframe) + estado del SFTP de publicación
  const [landingPreview, setLandingPreview] = useState(null); // { proposal, html } | null
  const [landingBusy, setLandingBusy] = useState({});         // id -> true mientras previsualiza/publica
  const [emailPreview, setEmailPreview] = useState(null);     // { proposal, html } | null (email de la campaña)
  const [sftpStatus, setSftpStatus] = useState(null);         // { configured, host, username, ... }
  const [sftpOpen, setSftpOpen] = useState(false);
  const [sftpForm, setSftpForm] = useState({ host: '', port: 22, username: '', privateKey: '', password: '', remoteBasePath: '/lp', publicBaseUrl: 'https://inter-conecta.com/lp' });
  const [sftpSaving, setSftpSaving] = useState(false);
  const [sftpTesting, setSftpTesting] = useState(false);

  // Agente Google Ads: conexión, cuentas, campañas (seguimiento), análisis y propuestas
  const [gadsStatus, setGadsStatus] = useState(null);          // { conectado, developerToken, oauth, apiVersion }
  const [gadsAccounts, setGadsAccounts] = useState([]);        // cuentas accesibles (para el selector del modal)
  const [gadsAccountsLoading, setGadsAccountsLoading] = useState(false);
  const [gadsCampaigns, setGadsCampaigns] = useState(null);    // { accounts: [{id,name,campaigns,error}], dateFrom, dateTo }
  const [gadsCampaignsLoading, setGadsCampaignsLoading] = useState(false);
  const [gadsRange, setGadsRange] = useState(() => ({
    from: new Date(Date.now() - 7 * 86400000).toISOString().slice(0, 10),
    to: new Date().toISOString().slice(0, 10)
  }));
  const [gadsProposals, setGadsProposals] = useState([]);
  const [gadsAnalysis, setGadsAnalysis] = useState([]);
  const [gadsBusy, setGadsBusy] = useState({});                // id propuesta -> true
  const [gadsChat, setGadsChat] = useState([]);                // [{role:'usuario'|'agente', text}]
  const [gadsChatInput, setGadsChatInput] = useState('');
  const [gadsChatBusy, setGadsChatBusy] = useState(false);
  const gadsChatEndRef = useRef(null);

  const fetchAll = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [agentsRes, tasksRes, logsRes] = await Promise.all([
        apiFetch('/api/agents', { headers: authHeaders }),
        apiFetch(`/api/agents/tasks?agent=${tab}`, { headers: authHeaders }),
        apiFetch(`/api/agents/logs?agent=${tab}&limit=30`, { headers: authHeaders })
      ]);
      if (agentsRes.status === 401 || tasksRes.status === 401 || logsRes.status === 401) return handleUnauthorized();
      const agentsData = await parseJsonSafe(agentsRes);
      const tasksData = await parseJsonSafe(tasksRes);
      const logsData = await parseJsonSafe(logsRes);
      if (!agentsRes.ok) throw new Error(agentsData.message || 'Error cargando agentes');
      setAgents(agentsData.agents || []);
      if (agentsData.proveedores) setProveedores(agentsData.proveedores);
      setClaudeDisponible(Boolean(agentsData.claudeDisponible));
      setTasks(Array.isArray(tasksData) ? tasksData : []);
      setLogs(Array.isArray(logsData) ? logsData : []);
      setError('');
    } catch (err) {
      if (!silent) setError(err.message || 'Error cargando los agentes');
    } finally {
      if (!silent) setLoading(false);
    }
  }, [authHeaders, tab]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  // Configuración del agente activo (indicaciones, tipos de campaña)
  const fetchSettings = useCallback(async () => {
    try {
      const res = await apiFetch(`/api/agents/settings/${tab}`, { headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (res.ok) {
        setSettings(data.config || {});
        setSettingsLoaded(true);
      }
    } catch (_e) { /* la config es opcional; sin carga el modal queda deshabilitado */ }
  }, [authHeaders, tab]);

  useEffect(() => { setSettings({}); setSettingsLoaded(false); setQuickCampaignTipo(''); fetchSettings(); }, [fetchSettings]);

  const fetchProposals = useCallback(async () => {
    if (tab !== 'captacion') return;
    try {
      const res = await apiFetch('/api/agents/captacion/proposals', { headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setProposals(data);
    } catch (_e) { /* opcional */ }
  }, [authHeaders, tab]);

  useEffect(() => { setProposals([]); fetchProposals(); }, [fetchProposals]);
  useEffect(() => {
    if (tab !== 'captacion') return undefined;
    const id = setInterval(fetchProposals, 20000);
    return () => clearInterval(id);
  }, [fetchProposals, tab]);

  const generateProposalsNow = async () => {
    if (proposalsGenerating) return;
    // Sin tipos de campaña el agente no puede proponer: avisamos claro en vez de lanzar en balde
    if ((settings.tiposCampana || []).length === 0) {
      setNotice({ type: 'warning', text: 'Primero define al menos un tipo de campaña en «Configurar»: el agente los usa para saber qué proponer y ofrecer.' });
      openSettings();
      return;
    }
    setProposalsGenerating(true);
    try {
      const res = await apiFetch('/api/agents/captacion/proposals/generate', { method: 'POST', headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo lanzar');
      setNotice({ type: 'info', text: 'Generando propuestas… suele tardar entre 10 y 30 segundos.' });
      // Comprobar el resultado: si el agente terminó sin generar nada (p.ej. una nota),
      // lo mostramos aquí en vez de dejarlo enterrado en el Historial.
      const checkResult = async () => {
        await fetchProposals();
        try {
          const lr = await apiFetch('/api/agents/logs?agent=captacion&limit=1', { headers: authHeaders });
          const logs = await parseJsonSafe(lr);
          const last = Array.isArray(logs) ? logs[0] : null;
          if (last && last.status !== 'running' && /propuesta|tipo de campaña|no pud/i.test(last.summary || last.error || '')) {
            setNotice({ type: last.status === 'error' ? 'danger' : 'info', text: last.summary || last.error });
          }
        } catch (_e) { /* opcional */ }
      };
      setTimeout(checkResult, 9000);
      setTimeout(checkResult, 22000);
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setProposalsGenerating(false);
    }
  };

  const sendProposalChat = async (proposal) => {
    const mensaje = (proposalChat[proposal.id] || '').trim();
    if (!mensaje || proposalBusy[proposal.id]) return;
    setProposalBusy((p) => ({ ...p, [proposal.id]: true }));
    try {
      const res = await apiFetch(`/api/agents/captacion/proposals/${proposal.id}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ mensaje })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo ajustar');
      // Merge (no reemplazo): la fila devuelta no trae los campos campaign_* del listado
      // (JOIN) y reemplazarla entera haría desaparecer el badge/chat del email hasta el
      // siguiente refresco.
      setProposals((prev) => prev.map((pr) => (pr.id === data.id ? { ...pr, ...data } : pr)));
      setProposalChat((p) => ({ ...p, [proposal.id]: '' }));
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setProposalBusy((p) => ({ ...p, [proposal.id]: false }));
    }
  };

  const approveProposal = async (proposal) => {
    if (!window.confirm(`¿Aprobar "${proposal.titulo}"? El agente captará los leads, obtendrá sus emails, creará la audiencia y dejará la campaña EN BORRADOR (nada se envía sin ti).`)) return;
    setProposalBusy((p) => ({ ...p, [proposal.id]: true }));
    try {
      const res = await apiFetch(`/api/agents/captacion/proposals/${proposal.id}/approve`, { method: 'POST', headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo aprobar');
      setNotice({ type: 'success', text: 'Propuesta aprobada: el agente la está ejecutando. Recibirás un aviso cuando la campaña esté en borrador.' });
      await fetchProposals();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setProposalBusy((p) => ({ ...p, [proposal.id]: false }));
    }
  };

  const fetchModels = useCallback(async () => {
    try {
      const res = await apiFetch('/api/agents/models', { headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (res.ok) setModelsPanel(data);
    } catch (_e) { /* opcional */ }
  }, [authHeaders]);

  useEffect(() => { if (modelsOpen && !modelsPanel) fetchModels(); }, [modelsOpen, modelsPanel, fetchModels]);

  const saveModel = async (componentKey, provider, model) => {
    setModelSaving(componentKey);
    try {
      const res = await apiFetch(`/api/agents/models/${componentKey}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ provider, model })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setModelsPanel((prev) => prev && ({
        ...prev,
        componentes: prev.componentes.map((c) => (c.key === componentKey ? { ...c, provider, model, fuente: 'app' } : c))
      }));
      setNotice({ type: 'success', text: 'Modelo actualizado.' });
      // Si tocamos un agente con badge de modelo, refrescamos su card
      if (componentKey === 'comercial' || componentKey === 'captacion') fetchAll({ silent: true });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setModelSaving('');
    }
  };

  const saveAnthropicKey = async (clear = false) => {
    if (anthropicSaving) return;
    setAnthropicSaving(true);
    try {
      const res = await apiFetch('/api/agents/anthropic-key', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ key: clear ? '' : anthropicKeyInput.trim() })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setAnthropicKeyInput('');
      setNotice({ type: 'success', text: clear ? 'Clave de Anthropic eliminada.' : 'Clave de Anthropic guardada: Claude ya está disponible.' });
      // Recargar el panel para que se habiliten/deshabiliten las opciones de Claude
      setModelsPanel(null);
      await fetchModels();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setAnthropicSaving(false);
    }
  };

  const fetchMemory = useCallback(async () => {
    try {
      const res = await apiFetch('/api/agents/memoria', { headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setMemoryNotes(data);
    } catch (_e) { /* opcional */ }
  }, [authHeaders]);

  useEffect(() => { if (subtab === 'memoria') fetchMemory(); }, [subtab, fetchMemory]);

  const searchMemoryClients = async (q) => {
    setMemoryForm((p) => ({ ...p, clientQuery: q, clientId: null }));
    if (q.trim().length < 2) { setMemoryClientOptions([]); return; }
    try {
      const res = await apiFetch(`/api/clients?search=${encodeURIComponent(q.trim())}&pageSize=8`, { headers: authHeaders });
      const data = await parseJsonSafe(res);
      const items = Array.isArray(data?.items) ? data.items : (Array.isArray(data) ? data : []);
      setMemoryClientOptions(items.map((c) => ({ id: c.id, nombre: c.razon_social })));
    } catch (_e) { /* sin sugerencias */ }
  };

  const saveMemoryNote = async () => {
    if (memorySaving) return;
    setMemorySaving(true);
    try {
      const body = memoryForm.scope === 'cliente'
        ? { scope: 'cliente', clientId: memoryForm.clientId, nota: memoryForm.nota }
        : memoryForm.scope === 'captacion'
          ? { scope: 'captacion', nota: memoryForm.nota }
          : { scope: 'sector', sector: memoryForm.sector, nota: memoryForm.nota };
      if (body.scope === 'cliente' && !body.clientId) throw new Error('Elige el cliente de la lista de sugerencias');
      const res = await apiFetch('/api/agents/memoria', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body)
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setMemoryForm({ scope: memoryForm.scope, clientQuery: '', clientId: null, sector: '', nota: '' });
      setMemoryClientOptions([]);
      await fetchMemory();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setMemorySaving(false);
    }
  };

  const saveMemoryEdit = async () => {
    if (!memoryEditing) return;
    try {
      const res = await apiFetch(`/api/agents/memoria/${memoryEditing.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ nota: memoryEditing.nota })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo editar');
      setMemoryEditing(null);
      await fetchMemory();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    }
  };

  const deleteMemoryNote = async (note) => {
    if (!window.confirm('¿Borrar esta nota de la memoria del agente?')) return;
    try {
      const res = await apiFetch(`/api/agents/memoria/${note.id}`, { method: 'DELETE', headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo borrar');
      await fetchMemory();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    }
  };

  const discardProposal = async (proposal) => {
    if (proposal.status !== 'completada') {
      setDiscardModal({ proposal, motivo: '', nota: '' });
      return;
    }
    if (!window.confirm(`¿Ocultar "${proposal.titulo}" del listado? La campaña/landing ya creadas no se tocan.`)) return;
    await submitDiscard(proposal, null, null);
  };

  const submitDiscard = async (proposal, motivo, nota) => {
    try {
      const res = await apiFetch(`/api/agents/captacion/proposals/${proposal.id}/discard`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ motivo: motivo || null, nota: nota || null })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo descartar');
      setDiscardModal(null);
      await fetchProposals();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    }
    return null;
  };

  // ── Landing de captación ──────────────────────────────────────────────
  const previewLanding = async (proposal) => {
    setLandingBusy((b) => ({ ...b, [proposal.id]: true }));
    try {
      const res = await apiFetch(`/api/agents/captacion/proposals/${proposal.id}/landing-preview`, { headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const html = await res.text();
      if (!res.ok) throw new Error('No se pudo generar la previsualización');
      setLandingPreview({ proposal, html });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message || 'No se pudo previsualizar la landing' });
    } finally {
      setLandingBusy((b) => ({ ...b, [proposal.id]: false }));
    }
  };

  // Previsualiza el EMAIL de la campaña. La primera vez el agente lo redacta (tarda unos
  // segundos) y queda cacheado: lo que apruebes será exactamente lo previsualizado.
  const previewEmail = async (proposal) => {
    const key = `em-${proposal.id}`;
    setLandingBusy((b) => ({ ...b, [key]: true }));
    try {
      const res = await apiFetch(`/api/agents/captacion/proposals/${proposal.id}/email-preview`, { headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const html = await res.text();
      if (!res.ok) throw new Error('No se pudo generar la previsualización del email');
      setEmailPreview({ proposal, html });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message || 'No se pudo previsualizar el email' });
    } finally {
      setLandingBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const publishLanding = async (proposal) => {
    setLandingBusy((b) => ({ ...b, [proposal.id]: true }));
    try {
      const res = await apiFetch(`/api/agents/captacion/proposals/${proposal.id}/publish-landing`, { method: 'POST', headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo publicar la landing');
      setNotice({ type: 'success', text: `Landing publicada: ${data.url}` });
      await fetchProposals();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setLandingBusy((b) => ({ ...b, [proposal.id]: false }));
    }
  };

  // ── SFTP de publicación de landings ───────────────────────────────────
  const fetchSftpStatus = useCallback(async () => {
    if (tab !== 'captacion') return;
    try {
      const res = await apiFetch('/api/agents/landing-sftp/status', { headers: authHeaders });
      if (res.ok) setSftpStatus(await parseJsonSafe(res));
    } catch (_e) { /* opcional */ }
  }, [authHeaders, tab]);

  useEffect(() => { fetchSftpStatus(); }, [fetchSftpStatus]);

  const saveSftp = async () => {
    if (sftpSaving) return;
    setSftpSaving(true);
    try {
      const res = await apiFetch('/api/agents/landing-sftp', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(sftpForm)
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar');
      setSftpStatus(data);
      setSftpOpen(false);
      setSftpForm((f) => ({ ...f, privateKey: '', password: '' })); // no retener secretos en memoria
      setNotice({ type: 'success', text: 'Publicación de landings configurada.' });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setSftpSaving(false);
    }
  };

  const testSftp = async () => {
    setSftpTesting(true);
    try {
      const res = await apiFetch('/api/agents/landing-sftp/test', { method: 'POST', headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (!res.ok || !data.ok) throw new Error(data.message || 'No se pudo conectar');
      setNotice({ type: 'success', text: `Conexión SFTP correcta (base ${data.remoteBasePath}${data.baseExists ? '' : ' — ¡no existe!'}).` });
    } catch (err) {
      setNotice({ type: 'danger', text: `SFTP: ${err.message}` });
    } finally {
      setSftpTesting(false);
    }
  };

  // ── Agente Google Ads ─────────────────────────────────────────────────
  const fetchGadsStatus = useCallback(async () => {
    if (tab !== 'google_ads') return;
    try {
      const res = await apiFetch('/api/agents/google-ads/status', { headers: authHeaders });
      if (res.ok) setGadsStatus(await parseJsonSafe(res));
    } catch (_e) { /* opcional */ }
  }, [authHeaders, tab]);

  const fetchGadsAccounts = async () => {
    setGadsAccountsLoading(true);
    try {
      const res = await apiFetch('/api/agents/google-ads/accounts', { headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudieron cargar las cuentas');
      setGadsAccounts(Array.isArray(data.items) ? data.items : []);
    } catch (err) {
      setNotice({ type: 'danger', text: `Google Ads: ${err.message}` });
    } finally {
      setGadsAccountsLoading(false);
    }
  };

  const gadsCampaignsReqRef = useRef(0);
  const fetchGadsCampaigns = useCallback(async () => {
    if (tab !== 'google_ads') return;
    // Guard de orden: si el usuario cambia el rango con una petición en vuelo, la respuesta
    // antigua no debe pisar a la nueva (mostraría datos de fechas que ya no son las elegidas).
    const reqId = gadsCampaignsReqRef.current + 1;
    gadsCampaignsReqRef.current = reqId;
    setGadsCampaignsLoading(true);
    try {
      const qs = new URLSearchParams({ dateFrom: gadsRange.from, dateTo: gadsRange.to });
      const res = await apiFetch(`/api/agents/google-ads/campaigns?${qs}`, { headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (reqId !== gadsCampaignsReqRef.current) return;
      if (!res.ok) throw new Error(data.message || 'No se pudieron cargar las campañas');
      setGadsCampaigns(data);
    } catch (err) {
      if (reqId !== gadsCampaignsReqRef.current) return;
      setNotice({ type: 'danger', text: `Google Ads: ${err.message}` });
      setGadsCampaigns({ accounts: [] });
    } finally {
      if (reqId === gadsCampaignsReqRef.current) setGadsCampaignsLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authHeaders, tab, gadsRange.from, gadsRange.to]);

  const fetchGadsProposals = useCallback(async () => {
    if (tab !== 'google_ads') return;
    try {
      const res = await apiFetch('/api/agents/google-ads/proposals', { headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setGadsProposals(data);
    } catch (_e) { /* opcional */ }
  }, [authHeaders, tab]);

  const fetchGadsAnalysis = useCallback(async () => {
    if (tab !== 'google_ads') return;
    try {
      const res = await apiFetch('/api/agents/google-ads/analysis?limit=40', { headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (res.ok && Array.isArray(data)) setGadsAnalysis(data);
    } catch (_e) { /* opcional */ }
  }, [authHeaders, tab]);

  useEffect(() => { fetchGadsStatus(); fetchGadsProposals(); }, [fetchGadsStatus, fetchGadsProposals]);
  useEffect(() => { if (tab === 'google_ads' && subtab === 'seguimiento') fetchGadsCampaigns(); }, [tab, subtab, fetchGadsCampaigns]);
  useEffect(() => { if (tab === 'google_ads' && subtab === 'analisis') fetchGadsAnalysis(); }, [tab, subtab, fetchGadsAnalysis]);

  const approveGadsProposal = async (p) => {
    if (!window.confirm(`¿Aplicar en Google Ads?\n\n${p.titulo}\n\nEsta acción escribe en la cuenta ${p.customer_name || p.customer_id}.`)) return;
    setGadsBusy((b) => ({ ...b, [p.id]: true }));
    try {
      const res = await apiFetch(`/api/agents/google-ads/proposals/${p.id}/approve`, { method: 'POST', headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo aplicar');
      setNotice({ type: 'success', text: 'Propuesta aplicada en Google Ads.' });
      await fetchGadsProposals();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
      await fetchGadsProposals();
    } finally {
      setGadsBusy((b) => ({ ...b, [p.id]: false }));
    }
  };

  const sendGadsChat = async () => {
    const mensaje = gadsChatInput.trim();
    if (!mensaje || gadsChatBusy) return;
    setGadsChatBusy(true);
    setGadsChat((c) => [...c, { role: 'usuario', text: mensaje }]);
    setGadsChatInput('');
    const scrollDown = () => requestAnimationFrame(() => gadsChatEndRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }));
    scrollDown();
    try {
      const history = gadsChat.slice(-10).map((m) => ({ role: m.role, text: m.text.slice(0, 1200) }));
      // El chat corre en segundo plano (puede tardar): lanzamos y consultamos el resultado.
      const res = await apiFetch('/api/agents/google-ads/chat', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ mensaje, history })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok || !data.jobId) throw new Error(data.message || 'No se pudo iniciar la consulta');
      // Polling del resultado hasta ~3 min (el agente puede encadenar varias consultas).
      let intentos = 0;
      const jobId = data.jobId;
      // eslint-disable-next-line no-constant-condition
      while (true) {
        await new Promise((r) => setTimeout(r, 2500));
        intentos += 1;
        let jr;
        try {
          const jres = await apiFetch(`/api/agents/google-ads/chat/${jobId}`, { headers: authHeaders });
          if (jres.status === 401) return handleUnauthorized();
          jr = await parseJsonSafe(jres);
          if (!jres.ok) throw new Error(jr.message || 'Error consultando el estado');
        } catch (_e) {
          if (intentos > 80) throw new Error('La consulta está tardando demasiado. Inténtalo de nuevo.');
          continue; // reintenta ante un fallo de red puntual
        }
        if (jr.status === 'done') {
          setGadsChat((c) => [...c, { role: 'agente', text: jr.respuesta || '(sin respuesta)' }]);
          if (jr.propuestasCreadas > 0) {
            setNotice({ type: 'success', text: `El agente ha creado ${jr.propuestasCreadas} propuesta(s): revísalas en la pestaña Propuestas (nada se aplica sin tu aprobación).` });
            fetchGadsProposals();
          }
          break;
        }
        if (jr.status === 'error') { throw new Error(jr.error || 'El agente no pudo responder'); }
        if (intentos > 80) throw new Error('La consulta está tardando demasiado. Inténtalo de nuevo.');
        scrollDown();
      }
    } catch (err) {
      setGadsChat((c) => [...c, { role: 'agente', text: `⚠️ ${err.message}` }]);
    } finally {
      setGadsChatBusy(false);
      scrollDown();
    }
  };

  const recheckGadsProposal = async (p) => {
    setGadsBusy((b) => ({ ...b, [p.id]: true }));
    try {
      const res = await apiFetch(`/api/agents/google-ads/proposals/${p.id}/postcheck`, { method: 'POST', headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo verificar');
      await fetchGadsProposals();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setGadsBusy((b) => ({ ...b, [p.id]: false }));
    }
  };

  const discardGadsProposal = async (p) => {
    if (!window.confirm(`¿Descartar la propuesta "${p.titulo}"? No se aplicará nada en Google Ads.`)) return;
    setGadsBusy((b) => ({ ...b, [p.id]: true }));
    try {
      const res = await apiFetch(`/api/agents/google-ads/proposals/${p.id}/discard`, { method: 'POST', headers: authHeaders });
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo descartar');
      await fetchGadsProposals();
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setGadsBusy((b) => ({ ...b, [p.id]: false }));
    }
  };

  const saveSettings = async () => {
    if (settingsSaving || !settingsDraft) return;
    setSettingsSaving(true);
    try {
      const res = await apiFetch(`/api/agents/settings/${tab}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ config: settingsDraft })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la configuración');
      setSettings(data.config || {});
      setSettingsOpen(false);
      setNotice({ type: 'success', text: 'Configuración del agente guardada.' });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setSettingsSaving(false);
    }
  };

  const openSettings = () => {
    if (!settingsLoaded) {
      // Sin la config cargada, guardar machacaría lo que hubiera: recargamos primero
      fetchSettings();
      setNotice({ type: 'warning', text: 'Cargando la configuración del agente… vuelve a intentarlo en un segundo.' });
      return;
    }
    const base = {
      provider: settings.provider || 'openai',
      model: settings.model || ''
    };
    if (tab === 'comercial') {
      setSettingsDraft({ ...base, indicacionesPresupuestos: settings.indicacionesPresupuestos || '' });
    } else if (tab === 'google_ads') {
      setSettingsDraft({
        ...base,
        trackedAccounts: Array.isArray(settings.trackedAccounts) ? settings.trackedAccounts.map((a) => ({ ...a })) : [],
        seguimiento: { enabled: false, hora: '09:00', umbral: 90, ...(settings.seguimiento || {}) },
        revision: { enabled: false, retencionDias: 90, ...(settings.revision || {}) },
        optimizacion: { enabled: false, frecuencia: 'semanal', ...(settings.optimizacion || {}) }
      });
      if (!gadsAccounts.length) fetchGadsAccounts(); // carga el selector de cuentas/MCC
    } else {
      setSettingsDraft({ ...base, tiposCampana: Array.isArray(settings.tiposCampana) ? settings.tiposCampana.map((t) => ({ ...t })) : [] });
    }
    setSettingsOpen(true);
  };

  // Refresco silencioso: mantiene al día el historial (las ejecuciones corren en segundo plano).
  useEffect(() => {
    const id = setInterval(() => fetchAll({ silent: true }), 20000);
    return () => clearInterval(id);
  }, [fetchAll]);

  const currentAgent = agents.find((a) => a.key === tab);

  const openNew = () => {
    setForm({ ...emptyForm, instrucciones: tab === 'captacion' ? '' : emptyForm.instrucciones });
    setModalOpen(true);
  };

  const openEdit = (task) => {
    setForm({
      id: task.id,
      titulo: task.titulo || '',
      instrucciones: task.instrucciones || '',
      scheduleType: task.schedule_type || 'diaria',
      runAtTime: task.run_at_time || '09:00',
      weekday: Number(task.weekday) || 1,
      intervalMinutes: Number(task.interval_minutes) || 60,
      enabled: task.enabled !== false
    });
    setModalOpen(true);
  };

  const submitTask = async (e) => {
    e.preventDefault();
    if (saving) return;
    setSaving(true);
    try {
      const body = {
        agentKey: tab,
        titulo: form.titulo,
        instrucciones: form.instrucciones,
        scheduleType: form.scheduleType,
        runAtTime: form.runAtTime,
        weekday: form.weekday,
        intervalMinutes: form.intervalMinutes,
        enabled: form.enabled
      };
      const res = await apiFetch(form.id ? `/api/agents/tasks/${form.id}` : '/api/agents/tasks', {
        method: form.id ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify(body)
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo guardar la tarea');
      setModalOpen(false);
      await fetchAll({ silent: true });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setSaving(false);
    }
  };

  const removeTask = async (task) => {
    if (!window.confirm(`¿Eliminar la tarea "${task.titulo}"?`)) return;
    try {
      const res = await apiFetch(`/api/agents/tasks/${task.id}`, { method: 'DELETE', headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo eliminar');
      await fetchAll({ silent: true });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    }
  };

  const toggleEnabled = async (task) => {
    try {
      const res = await apiFetch(`/api/agents/tasks/${task.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          agentKey: task.agent_key,
          titulo: task.titulo,
          instrucciones: task.instrucciones,
          scheduleType: task.schedule_type,
          runAtTime: task.run_at_time,
          weekday: task.weekday,
          intervalMinutes: task.interval_minutes,
          enabled: !task.enabled
        })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo actualizar');
      await fetchAll({ silent: true });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    }
  };

  const runNow = async (task) => {
    if (runningTaskId) return;
    setRunningTaskId(task.id);
    setNotice(null);
    try {
      const res = await apiFetch(`/api/agents/tasks/${task.id}/run-now`, { method: 'POST', headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo lanzar');
      setNotice({ type: 'success', text: 'Ejecución lanzada: el resultado aparecerá en el historial en unos minutos.' });
      await fetchAll({ silent: true });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setRunningTaskId(null);
    }
  };

  const runQuickOrder = async (e) => {
    e.preventDefault();
    if (quickRunning || !quickOrder.trim()) return;
    setQuickRunning(true);
    setNotice(null);
    try {
      const res = await apiFetch('/api/agents/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          agentKey: tab,
          instrucciones: quickOrder.trim(),
          config: tab === 'captacion' && quickCampaignTipo ? { campanaTipoId: Number(quickCampaignTipo) } : undefined
        })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo lanzar');
      setQuickOrder('');
      setNotice({ type: 'success', text: 'Orden lanzada: sigue el progreso en el historial (se refresca solo).' });
      await fetchAll({ silent: true });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setQuickRunning(false);
    }
  };

  // Pasada puntual del agente comercial sobre las derivadas pendientes (sin tarea guardada).
  const runComercialNow = async () => {
    if (quickRunning) return;
    setQuickRunning(true);
    setNotice(null);
    try {
      const res = await apiFetch('/api/agents/run', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ agentKey: 'comercial' })
      });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo lanzar');
      setNotice({ type: 'success', text: 'Pasada lanzada: las respuestas aparecerán en /comunicaciones y el resultado en el historial.' });
      await fetchAll({ silent: true });
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    } finally {
      setQuickRunning(false);
    }
  };

  const openLogDetail = async (log) => {
    try {
      const res = await apiFetch(`/api/agents/logs/${log.id}`, { headers: authHeaders });
      if (res.status === 401) return handleUnauthorized();
      const data = await parseJsonSafe(res);
      if (!res.ok) throw new Error(data.message || 'No se pudo cargar el detalle');
      setLogDetail(data);
    } catch (err) {
      setNotice({ type: 'danger', text: err.message });
    }
  };

  const renderLogDetail = (detail) => {
    if (!detail) return <p className="text-muted small mb-0">Sin detalle.</p>;
    const data = typeof detail === 'string' ? (() => { try { return JSON.parse(detail); } catch (_e) { return null; } })() : detail;
    if (!data) return <p className="text-muted small mb-0">Sin detalle.</p>;
    if (Array.isArray(data.resultados) && data.plan) {
      return (
        <>
          <p className="small mb-2">
            <strong>Plan:</strong> {data.plan.sector} · {data.plan.country} · {Array.isArray(data.plan.locations) ? data.plan.locations.length : 0} localización(es)
            {data.plan.nota && <span className="text-warning d-block">Nota: {data.plan.nota}</span>}
          </p>
          <div className="table-responsive">
            <table className="table table-sm table-striped">
              <thead>
                <tr><th>Localización</th><th>Encontrados</th><th>Importados</th><th>Ya existían</th><th>Emails</th><th></th></tr>
              </thead>
              <tbody>
                {data.resultados.map((r, idx) => (
                  <tr key={idx}>
                    <td>{r.location}</td>
                    <td>{r.encontrados}</td>
                    <td>{r.importados}</td>
                    <td>{r.duplicados}</td>
                    <td>{r.emails_encontrados}</td>
                    <td className="text-danger small">{r.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      );
    }
    if (Array.isArray(data.resultados)) {
      return (
        <div className="table-responsive">
          <table className="table table-sm table-striped">
            <thead>
              <tr><th>Email</th><th>Asunto</th><th>Resultado</th><th>Presupuesto</th></tr>
            </thead>
            <tbody>
              {data.resultados.map((r, idx) => (
                <tr key={idx}>
                  <td className="small">{r.remitente || r.messageId}</td>
                  <td className="small">{r.asunto || '-'}</td>
                  <td className="small">{r.ok ? (r.resumen || 'Respuesta redactada') : <span className="text-danger">{r.error}</span>}</td>
                  <td className="small">{r.presupuesto ? `${r.presupuesto}${r.presupuesto_total ? ` (${Number(r.presupuesto_total).toLocaleString('es-ES', { style: 'currency', currency: 'EUR' })})` : ''}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );
    }
    return <pre className="small bg-light p-2 rounded" style={{ maxHeight: 300, overflow: 'auto' }}>{JSON.stringify(data, null, 2)}</pre>;
  };

  return (
    <div className="container-fluid py-3 agents-page">
      <style>{`
        .agents-page .agents-card, .agents-page .card { border: 1px solid #e9edf3; border-radius: 12px; box-shadow: 0 1px 3px rgba(16,24,40,0.04); }
        .agents-page .card-header { background: #fbfcfe; border-bottom: 1px solid #eef1f6; font-weight: 600; border-radius: 12px 12px 0 0; }
        .agents-segmented { display: inline-flex; background: #eef1f6; border-radius: 10px; padding: 4px; gap: 4px; }
        .agents-seg-btn { border: none; background: transparent; border-radius: 8px; padding: 8px 18px; font-weight: 600; font-size: .9rem; color: #5b6472; display: inline-flex; align-items: center; gap: 8px; transition: all .15s; }
        .agents-seg-btn:hover { color: #1a2348; }
        .agents-seg-btn.active { background: #fff; color: #2f77c4; box-shadow: 0 1px 3px rgba(16,24,40,.1); }
        .agents-hero { display: flex; flex-wrap: wrap; gap: 20px; align-items: center; justify-content: space-between; background: linear-gradient(135deg, #ffffff 0%, #f4f8ff 100%); border: 1px solid #e4ebf5; border-radius: 14px; padding: 20px 22px; }
        .agents-hero-main { display: flex; gap: 16px; align-items: flex-start; flex: 1 1 340px; min-width: 280px; }
        .agents-hero-avatar { width: 54px; height: 54px; border-radius: 14px; display: flex; align-items: center; justify-content: center; color: #fff; flex-shrink: 0; }
        .agents-hero-avatar.comercial { background: linear-gradient(135deg, #2f77c4, #1a2348); }
        .agents-hero-avatar.captacion { background: linear-gradient(135deg, #3ba4f5, #2f77c4); }
        .agents-model-pill { display: inline-flex; align-items: center; background: #eaf2fd; color: #2f77c4; font-size: .72rem; font-weight: 600; padding: 3px 9px; border-radius: 20px; }
        .agents-chip { display: inline-flex; align-items: center; gap: 4px; background: #eef4fc; color: #3a6ea5; border: 1px solid #dce8f7; font-size: .72rem; font-weight: 500; padding: 3px 9px; border-radius: 20px; }
        .agents-chip.more { background: #f0f2f5; color: #6b7684; border-color: #e2e6ec; }
        .agents-hero-stats { display: flex; gap: 10px; }
        .agents-stat { background: #fff; border: 1px solid #e7edf5; border-radius: 10px; padding: 10px 16px; text-align: center; min-width: 92px; }
        .agents-stat-num { font-size: 1.5rem; font-weight: 700; line-height: 1.1; }
        .agents-stat-lbl { font-size: .68rem; color: #7a8595; text-transform: uppercase; letter-spacing: .3px; margin-top: 2px; }
        .agents-subnav { display: flex; flex-wrap: wrap; gap: 6px; border-bottom: 2px solid #eef1f6; }
        .agents-subnav-btn { border: none; background: transparent; padding: 9px 14px; font-weight: 600; font-size: .87rem; color: #6b7684; display: inline-flex; align-items: center; gap: 7px; border-bottom: 2px solid transparent; margin-bottom: -2px; transition: all .15s; }
        .agents-subnav-btn:hover { color: #2f77c4; }
        .agents-subnav-btn.active { color: #2f77c4; border-bottom-color: #2f77c4; }
        .agents-subnav-badge { background: #2f77c4; color: #fff; font-size: .65rem; font-weight: 700; border-radius: 20px; padding: 1px 7px; }
        .agents-hero-avatar.google_ads { background: linear-gradient(135deg, #34a853, #1a73e8); }
        /* Tablas anchas (campañas Google Ads, historiales): scroll horizontal suave en móvil */
        .agents-page .table-responsive { -webkit-overflow-scrolling: touch; }
        .gads-date { width: 150px; }

        @media (max-width: 640px) {
          /* Selector de agentes: una fila deslizable en vez de desbordar/romper */
          .agents-segmented { display: flex; width: 100%; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .agents-segmented::-webkit-scrollbar { display: none; }
          .agents-seg-btn { flex: 0 0 auto; padding: 8px 12px; font-size: .82rem; white-space: nowrap; }
          /* Hero: apilado y a lo ancho */
          .agents-hero { padding: 14px; gap: 14px; }
          .agents-hero-main { flex: 1 1 100%; min-width: 0; gap: 12px; }
          .agents-hero-avatar { width: 44px; height: 44px; }
          /* Grid de columnas iguales: N stats siempre caben sin desbordar (sea 2 o 3).
             minmax(0,1fr) — no 1fr — para que las columnas se repartan y el texto envuelva. */
          .agents-hero-stats { width: 100%; display: grid; grid-auto-flow: column; grid-auto-columns: minmax(0, 1fr); gap: 8px; }
          .agents-stat { min-width: 0; padding: 8px 4px; }
          .agents-stat-num { font-size: 1.2rem; }
          .agents-stat-lbl { font-size: .6rem; }
          /* Subnav de pestañas: una fila deslizable */
          .agents-subnav { flex-wrap: nowrap; overflow-x: auto; -webkit-overflow-scrolling: touch; }
          .agents-subnav::-webkit-scrollbar { display: none; }
          .agents-subnav-btn { flex: 0 0 auto; padding: 9px 11px; font-size: .82rem; white-space: nowrap; }
          /* Selector de fechas de campañas: ocupa el ancho disponible */
          .gads-date { width: auto; flex: 1 1 44%; min-width: 0; }
        }
      `}</style>
      <div className="d-flex align-items-center justify-content-between mb-3">
        <h4 className="mb-0"><BsRobot className="me-2 text-primary" />Agentes IA</h4>
        <button className="btn btn-outline-secondary btn-sm" onClick={() => fetchAll()} title="Refrescar">
          <BsArrowRepeat />
        </button>
      </div>

      <div className="card mb-3">
        <div className="card-header d-flex justify-content-between align-items-center" style={{ cursor: 'pointer' }} onClick={() => setModelsOpen((v) => !v)}>
          <span className="d-flex align-items-center"><BsCpu className="me-2" />Modelos de IA de la app</span>
          <button className="btn btn-sm btn-outline-secondary" onClick={(e) => { e.stopPropagation(); setModelsOpen((v) => !v); }}>
            {modelsOpen ? 'Ocultar' : 'Configurar modelos'}
          </button>
        </div>
        {modelsOpen && (
          <div className="card-body">
            <p className="text-muted small mb-2">
              Elige el modelo de IA de cada parte de la app, sin tocar variables del servidor. El cambio aplica en caliente
              (unos segundos). «app» = fijado aquí · «servidor»/«por defecto» = todavía no configurado desde la app.
            </p>
            <div className="border rounded p-2 mb-3 bg-light">
              <div className="d-flex flex-wrap align-items-center gap-2">
                <strong className="small">Clave de Anthropic (Claude):</strong>
                {modelsPanel?.anthropic?.configured ? (
                  <span className="badge bg-success">Configurada{modelsPanel.anthropic.source === 'env' ? ' (servidor)' : ' (app)'}</span>
                ) : (
                  <span className="badge bg-secondary">No configurada · Claude deshabilitado</span>
                )}
              </div>
              <div className="d-flex flex-wrap gap-2 mt-2 align-items-center">
                <input
                  type="password"
                  className="form-control form-control-sm"
                  style={{ maxWidth: 360 }}
                  placeholder="sk-ant-... (se guarda cifrada; no se vuelve a mostrar)"
                  value={anthropicKeyInput}
                  onChange={(e) => setAnthropicKeyInput(e.target.value)}
                  autoComplete="new-password"
                />
                <button className="btn btn-sm btn-primary" onClick={() => saveAnthropicKey(false)} disabled={anthropicSaving || !anthropicKeyInput.trim()}>
                  {anthropicSaving ? 'Guardando…' : 'Guardar clave'}
                </button>
                {modelsPanel?.anthropic?.configured && modelsPanel?.anthropic?.source === 'app' && (
                  <button className="btn btn-sm btn-outline-danger" onClick={() => saveAnthropicKey(true)} disabled={anthropicSaving}>
                    Eliminar
                  </button>
                )}
              </div>
              <div className="form-text">
                Pégala aquí y podrás elegir Claude como «cerebro» de los agentes comercial y captación. Se guarda cifrada en la
                base de datos; por seguridad no se muestra de nuevo, solo puedes reemplazarla o eliminarla.
              </div>
            </div>
            {!modelsPanel ? (
              <div className="text-center py-3"><div className="spinner-border spinner-border-sm" /></div>
            ) : (
              Array.from(new Set(modelsPanel.componentes.map((c) => c.grupo))).map((grupo) => (
                <div className="mb-3" key={grupo}>
                  <div className="fw-bold small text-uppercase text-muted mb-1">{grupo}</div>
                  <div className="table-responsive">
                    <table className="table table-sm align-middle mb-0">
                      <tbody>
                        {modelsPanel.componentes.filter((c) => c.grupo === grupo).map((c) => {
                          const modelos = modelsPanel.proveedores[c.provider]?.modelos || modelsPanel.proveedores.openai.modelos;
                          return (
                            <tr key={c.key}>
                              <td style={{ minWidth: 200 }}>
                                <div className="small fw-semibold">{c.label}</div>
                                <div className="text-muted" style={{ fontSize: '0.75rem' }}>{c.descripcion}</div>
                              </td>
                              {c.providerConfigurable && (
                                <td style={{ width: 160 }}>
                                  <select
                                    className="form-select form-select-sm"
                                    value={c.provider}
                                    onChange={(e) => {
                                      const provider = e.target.value;
                                      const firstModel = (modelsPanel.proveedores[provider]?.modelos || [])[0];
                                      saveModel(c.key, provider, firstModel);
                                    }}
                                    disabled={modelSaving === c.key}
                                  >
                                    {Object.entries(modelsPanel.proveedores).map(([k, prov]) => (
                                      <option key={k} value={k} disabled={k === 'claude' && !modelsPanel.claudeDisponible}>
                                        {prov.nombre}{k === 'claude' && !modelsPanel.claudeDisponible ? ' (falta clave)' : ''}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              )}
                              <td style={{ width: 200 }}>
                                <select
                                  className="form-select form-select-sm"
                                  value={c.model}
                                  onChange={(e) => saveModel(c.key, c.provider, e.target.value)}
                                  disabled={modelSaving === c.key}
                                >
                                  {!modelos.includes(c.model) && <option value={c.model}>{c.model}</option>}
                                  {modelos.map((m) => <option key={m} value={m}>{m}</option>)}
                                </select>
                              </td>
                              <td style={{ width: 90 }}>
                                <span className={`badge ${c.fuente === 'app' ? 'bg-success' : c.fuente === 'env' ? 'bg-secondary' : 'bg-light text-dark border'}`}>
                                  {c.fuente === 'app' ? 'app' : c.fuente === 'env' ? 'servidor' : 'por defecto'}
                                </span>
                                {modelSaving === c.key && <span className="spinner-border spinner-border-sm ms-1" />}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      <div className="agents-segmented mb-3">
        {[{ key: 'comercial', label: 'Agente comercial', icon: <BsBriefcase /> }, { key: 'captacion', label: 'Agente de captación', icon: <BsBinoculars /> }, { key: 'google_ads', label: 'Agente Google Ads', icon: <BsGraphUpArrow /> }].map((t) => (
          <button
            key={t.key}
            className={`agents-seg-btn ${tab === t.key ? 'active' : ''}`}
            onClick={() => { setTab(t.key); setSubtab('resumen'); }}
          >
            {t.icon} <span>{t.label}</span>
          </button>
        ))}
      </div>

      {error && <div className="alert alert-danger py-2">{error}</div>}
      {notice && (
        <div className={`alert alert-${notice.type} py-2 d-flex justify-content-between align-items-center`}>
          <span>{notice.text}</span>
          <button className="btn-close" onClick={() => setNotice(null)}></button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-5"><div className="spinner-border" role="status"></div></div>
      ) : (
        <>
          {currentAgent && (
            <div className="agents-hero mb-3">
              <div className="agents-hero-main">
                <div className={`agents-hero-avatar ${tab}`}>
                  {tab === 'comercial' ? <BsBriefcase size={26} /> : tab === 'google_ads' ? <BsGraphUpArrow size={26} /> : <BsBinoculars size={26} />}
                </div>
                <div className="agents-hero-body">
                  <div className="d-flex align-items-center gap-2 flex-wrap mb-1">
                    <h5 className="mb-0 fw-bold">{currentAgent.nombre}</h5>
                    {currentAgent.modelo && (
                      <span className="agents-model-pill" title="Modelo de IA que usa este agente">
                        <BsCpu size={11} className="me-1" />{currentAgent.modelo}
                      </span>
                    )}
                  </div>
                  <p className="text-muted small mb-2">{currentAgent.descripcion}</p>
                  <div className="d-flex flex-wrap gap-1 align-items-center">
                    {(currentAgent.capacidades?.herramientas || []).slice(0, 4).map((h) => (
                      <span key={h.nombre} className="agents-chip tool" title={h.descripcion}>
                        <BsTools size={10} />{h.titulo}
                      </span>
                    ))}
                    {(currentAgent.capacidades?.herramientas || []).length > 4 && (
                      <span className="agents-chip more">+{currentAgent.capacidades.herramientas.length - 4}</span>
                    )}
                    <button className="btn btn-link btn-sm p-0 ms-1 text-decoration-none" onClick={() => setCapsOpen(true)}>
                      Ver capacidades
                    </button>
                    <button className="btn btn-link btn-sm p-0 ms-2 d-inline-flex align-items-center text-decoration-none" onClick={openSettings}>
                      <BsGear className="me-1" size={12} />Configurar
                    </button>
                  </div>
                </div>
              </div>
              <div className="agents-hero-stats">
                {tab === 'comercial' && (
                  <>
                    <div className="agents-stat">
                      <div className="agents-stat-num text-primary">{currentAgent.stats?.derivadas_pendientes ?? 0}</div>
                      <div className="agents-stat-lbl">Derivadas pendientes</div>
                    </div>
                    <div className="agents-stat">
                      <div className="agents-stat-num">{currentAgent.stats?.procesadas ?? 0}</div>
                      <div className="agents-stat-lbl">Procesadas</div>
                    </div>
                    {(currentAgent.stats?.aprobadas_sin_tocar ?? 0) + (currentAgent.stats?.aprobadas_editadas ?? 0) > 0 && (
                      <div className="agents-stat" title="Respuestas aprobadas tal cual vs editadas antes de enviar">
                        <div className="agents-stat-num text-success">
                          {Math.round((100 * (currentAgent.stats?.aprobadas_sin_tocar ?? 0)) /
                            ((currentAgent.stats?.aprobadas_sin_tocar ?? 0) + (currentAgent.stats?.aprobadas_editadas ?? 0)))}%
                        </div>
                        <div className="agents-stat-lbl">Aprobadas sin editar</div>
                      </div>
                    )}
                  </>
                )}
                {tab === 'captacion' && (
                  <>
                    <div className="agents-stat">
                      <div className="agents-stat-num text-primary">{currentAgent.stats?.targets ?? 0}</div>
                      <div className="agents-stat-lbl">Empresas captadas</div>
                    </div>
                    <div className="agents-stat">
                      <div className="agents-stat-num text-success">{currentAgent.stats?.con_email ?? 0}</div>
                      <div className="agents-stat-lbl">Con email</div>
                    </div>
                  </>
                )}
                {tab === 'google_ads' && (
                  <>
                    <div className="agents-stat">
                      <div className="agents-stat-num text-primary">{currentAgent.stats?.cuentas ?? 0}</div>
                      <div className="agents-stat-lbl">Cuentas en seguimiento</div>
                    </div>
                    <div className="agents-stat">
                      <div className={`agents-stat-num ${(currentAgent.stats?.propuestas_pendientes ?? 0) > 0 ? 'text-warning' : ''}`}>{currentAgent.stats?.propuestas_pendientes ?? 0}</div>
                      <div className="agents-stat-lbl">Propuestas pendientes</div>
                    </div>
                    <div className="agents-stat" title="Conexión con la API de Google Ads (OAuth + developer token del panel)">
                      <div className={`agents-stat-num ${gadsStatus?.conectado ? 'text-success' : 'text-danger'}`}>{gadsStatus?.conectado ? '✓' : '✗'}</div>
                      <div className="agents-stat-lbl">API {gadsStatus?.apiVersion || ''}</div>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {currentAgent && (
            <div className="agents-subnav mb-3">
              {[
                { key: 'resumen', label: 'Resumen', icon: <BsSpeedometer2 /> },
                ...(tab === 'captacion' ? [{ key: 'propuestas', label: 'Propuestas', icon: <BsLightbulb />, badge: proposals.filter((p) => ['propuesta', 'en_ajuste'].includes(p.status)).length }] : []),
                ...(tab === 'google_ads' ? [
                  { key: 'seguimiento', label: 'Campañas', icon: <BsBarChartLine /> },
                  { key: 'chat-gads', label: 'Chat', icon: <BsRobot /> },
                  { key: 'analisis', label: 'Análisis', icon: <BsJournalText /> },
                  { key: 'propuestas-gads', label: 'Propuestas', icon: <BsLightbulb />, badge: gadsProposals.filter((p) => p.status === 'propuesta').length }
                ] : []),
                ...(['comercial', 'captacion'].includes(tab) ? [{ key: 'memoria', label: 'Memoria', icon: <BsJournalText />, badge: memoryNotes.length }] : []),
                { key: 'tareas', label: 'Tareas', icon: <BsCalendar2Check />, badge: tasks.length },
                { key: 'historial', label: 'Historial', icon: <BsClockHistory /> }
              ].map((sc) => (
                <button key={sc.key} className={`agents-subnav-btn ${subtab === sc.key ? 'active' : ''}`} onClick={() => setSubtab(sc.key)}>
                  {sc.icon}<span>{sc.label}</span>
                  {sc.badge > 0 && <span className="agents-subnav-badge">{sc.badge}</span>}
                </button>
              ))}
            </div>
          )}

          {tab === 'google_ads' && subtab === 'resumen' && (
            <div className="alert alert-light border small mb-3">
              Vigila tus cuentas de Google Ads con un ciclo diario a la hora que elijas: <strong>seguimiento</strong> del % de
              optimización (push si alguna campaña baja del umbral), <strong>revisión</strong> con métricas guardadas y análisis IA
              (diario + global con histórico) y <strong>propuestas de optimización</strong> que solo se aplican con tu aprobación.
              Configura las cuentas y los interruptores en «Configurar»; la tarea diaria se programa sola. La conexión (OAuth +
              developer token) se gestiona en <Link to="/google-ads">/google-ads</Link>.
            </div>
          )}
          {tab === 'comercial' && subtab === 'resumen' && (
            <div className="alert alert-light border small mb-3 d-flex flex-wrap justify-content-between align-items-center gap-2">
              <span>
                El agente de email etiqueta como <span className="badge bg-info text-dark">Agente comercial</span> los correos con
                oportunidad comercial. Este agente los recoge (según la programación de abajo o con «Ejecutar ahora»), redacta la
                respuesta y crea el presupuesto si procede: todo queda <strong>pendiente de tu aprobación</strong> en{' '}
                <Link to="/comunicaciones">/comunicaciones</Link>.
              </span>
              <button
                className="btn btn-sm btn-primary d-flex align-items-center flex-shrink-0"
                onClick={runComercialNow}
                disabled={quickRunning || (currentAgent?.stats?.derivadas_pendientes ?? 0) === 0}
                title={(currentAgent?.stats?.derivadas_pendientes ?? 0) === 0 ? 'No hay comunicaciones derivadas pendientes' : 'Procesar ahora las derivadas pendientes'}
              >
                {quickRunning ? <span className="spinner-border spinner-border-sm me-1" /> : <BsPlayFill className="me-1" />}
                Procesar {currentAgent?.stats?.derivadas_pendientes ?? 0} derivada(s) ahora
              </button>
            </div>
          )}

          {tab === 'captacion' && subtab === 'resumen' && (
            <div className="card agents-card mb-3">
              <div className="card-body">
                <h6 className="mb-2 d-flex align-items-center"><BsPlayFill className="me-1 text-primary" />Orden puntual</h6>
                <form className="d-flex gap-2 flex-wrap" onSubmit={runQuickOrder}>
                  <input
                    className="form-control"
                    style={{ flex: '1 1 320px' }}
                    placeholder='Ej.: "capta peluquerías en Madrid", "toda España", "repasa los emails pendientes"'
                    value={quickOrder}
                    onChange={(e) => setQuickOrder(e.target.value)}
                  />
                  <select
                    className="form-select"
                    style={{ maxWidth: 260 }}
                    value={quickCampaignTipo}
                    onChange={(e) => setQuickCampaignTipo(e.target.value)}
                    title="Si eliges un tipo, el agente creará además una campaña de email marketing EN BORRADOR con los leads captados"
                  >
                    <option value="">Sin campaña</option>
                    {(settings.tiposCampana || []).map((t) => (
                      <option key={t.id} value={t.id}>+ Campaña: {t.titulo}</option>
                    ))}
                  </select>
                  <button className="btn btn-primary d-flex align-items-center" type="submit" disabled={quickRunning || !quickOrder.trim()}>
                    {quickRunning ? <span className="spinner-border spinner-border-sm me-1" /> : <BsPlayFill className="me-1" />}
                    Ejecutar
                  </button>
                </form>
                <div className="form-text">
                  El agente trocea la orden por ciudades/provincias, busca en Google Places, importa a{' '}
                  <Link to="/captacion-business">/captacion-business</Link> y rastrea el email de cada empresa nueva.
                  {(settings.tiposCampana || []).length === 0 && (
                    <> Para poder añadir campañas, define los <button type="button" className="btn btn-link btn-sm p-0 align-baseline" onClick={openSettings}>tipos de campaña</button>.</>
                  )}
                  {' '}Las campañas quedan <strong>en borrador</strong> en <Link to="/email-marketing">/email-marketing</Link>: nada se envía sin tu OK.
                </div>
              </div>
            </div>
          )}

          {tab === 'captacion' && subtab === 'propuestas' && (
            <div className="card agents-card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="d-flex align-items-center"><BsLightbulb className="me-2 text-warning" />Propuestas de captación {proposals.filter((p) => ['propuesta', 'en_ajuste'].includes(p.status)).length > 0 && (
                  <span className="badge bg-primary ms-1">{proposals.filter((p) => ['propuesta', 'en_ajuste'].includes(p.status)).length}</span>
                )}</span>
                <button className="btn btn-sm btn-outline-primary" onClick={generateProposalsNow} disabled={proposalsGenerating}>
                  {proposalsGenerating ? 'Generando…' : 'Generar propuestas ahora'}
                </button>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-2">
                  El agente estudia lo ya enviado (no repite audiencias con envío hace menos de 14 días), tus tipos de campaña y la
                  cobertura de captación, y propone campañas (~20% reenganche · ~80% audiencias nuevas). Ajusta la que te guste por
                  chat y apruébala: el agente lo hará todo y la campaña quedará en borrador. Programable con la orden
                  «propón campañas de captación» (p.ej. cada día a las 9:00).
                </p>

                {/* Publicación de landings (SFTP): cada propuesta genera una landing a medida
                    que se publica en inter-conecta.com/lp/<slug>/ y su enlace va en el CTA del email. */}
                <div className="border rounded p-2 mb-2 bg-light">
                  <div className="d-flex flex-wrap align-items-center gap-2">
                    <strong className="small"><BsGlobe className="me-1" size={13} />Publicación de landings</strong>
                    {sftpStatus?.configured ? (
                      <span className="badge bg-success">Activa · {sftpStatus.username}@{sftpStatus.host} → {sftpStatus.publicBaseUrl}</span>
                    ) : (
                      <span className="badge bg-secondary">Sin configurar</span>
                    )}
                    <div className="ms-auto d-flex gap-2">
                      {sftpStatus?.configured && (
                        <button className="btn btn-sm btn-outline-secondary" onClick={testSftp} disabled={sftpTesting}>
                          {sftpTesting ? <span className="spinner-border spinner-border-sm" /> : 'Probar conexión'}
                        </button>
                      )}
                      <button className="btn btn-sm btn-outline-primary" onClick={() => setSftpOpen((o) => !o)}>
                        {sftpOpen ? 'Cerrar' : (sftpStatus?.configured ? 'Cambiar' : 'Configurar')}
                      </button>
                    </div>
                  </div>
                  <p className="text-muted small mb-0 mt-1">
                    Al aprobar una propuesta, se publica su landing y ese enlace se usa en el botón del email. La landing se retira sola 30 días después de enviarse la campaña.
                  </p>
                  {sftpOpen && (
                    <div className="row g-2 mt-1">
                      <div className="col-md-6"><label className="form-label small mb-0">Host</label>
                        <input className="form-control form-control-sm" value={sftpForm.host} onChange={(e) => setSftpForm((f) => ({ ...f, host: e.target.value }))} placeholder="161.22.41.121" /></div>
                      <div className="col-md-2"><label className="form-label small mb-0">Puerto</label>
                        <input className="form-control form-control-sm" type="number" value={sftpForm.port} onChange={(e) => setSftpForm((f) => ({ ...f, port: e.target.value }))} /></div>
                      <div className="col-md-4"><label className="form-label small mb-0">Usuario</label>
                        <input className="form-control form-control-sm" value={sftpForm.username} onChange={(e) => setSftpForm((f) => ({ ...f, username: e.target.value }))} placeholder="ic_lp" /></div>
                      <div className="col-12"><label className="form-label small mb-0">Clave privada SSH (recomendado) — se guarda cifrada; déjala vacía para conservar la ya guardada</label>
                        <textarea className="form-control form-control-sm font-monospace" rows={3} value={sftpForm.privateKey} onChange={(e) => setSftpForm((f) => ({ ...f, privateKey: e.target.value }))} placeholder="-----BEGIN OPENSSH PRIVATE KEY-----" style={{ fontSize: '0.72rem' }} /></div>
                      <div className="col-md-6"><label className="form-label small mb-0">…o contraseña</label>
                        <input className="form-control form-control-sm" type="password" value={sftpForm.password} onChange={(e) => setSftpForm((f) => ({ ...f, password: e.target.value }))} autoComplete="new-password" /></div>
                      <div className="col-md-3"><label className="form-label small mb-0">Ruta remota</label>
                        <input className="form-control form-control-sm" value={sftpForm.remoteBasePath} onChange={(e) => setSftpForm((f) => ({ ...f, remoteBasePath: e.target.value }))} placeholder="/lp" /></div>
                      <div className="col-md-3"><label className="form-label small mb-0">URL pública base</label>
                        <input className="form-control form-control-sm" value={sftpForm.publicBaseUrl} onChange={(e) => setSftpForm((f) => ({ ...f, publicBaseUrl: e.target.value }))} placeholder="https://inter-conecta.com/lp" /></div>
                      <div className="col-12">
                        <button className="btn btn-sm btn-primary" onClick={saveSftp} disabled={sftpSaving}>
                          {sftpSaving ? 'Guardando…' : 'Guardar credenciales'}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {(settings.tiposCampana || []).length === 0 && (
                  <div className="alert alert-warning py-2 small d-flex flex-wrap align-items-center justify-content-between gap-2">
                    <span>
                      <strong>Antes de generar propuestas, define tus «tipos de campaña».</strong> Son los servicios que ofreces
                      (título + oferta, p.ej. «diseño web · desde 250€ con SEO»): el agente los usa para saber qué proponer y a quién.
                    </span>
                    <button className="btn btn-sm btn-warning flex-shrink-0 d-inline-flex align-items-center" onClick={openSettings}>
                      <BsGear className="me-1" />Definir tipos de campaña
                    </button>
                  </div>
                )}
                {/* Las completadas se pliegan tras un toggle para que el listado no se llene de históricas */}
                {(() => {
                  const completadasCount = proposals.filter((p) => p.status === 'completada').length;
                  if (!completadasCount) return null;
                  return (
                    <div className="d-flex align-items-center gap-2 mb-2 small">
                      <span className="text-muted">{completadasCount} completada(s)</span>
                      <button className="btn btn-link btn-sm p-0 text-decoration-none" onClick={() => setShowCompletadas((s) => !s)}>
                        {showCompletadas ? 'Ocultar' : 'Mostrar'}
                      </button>
                    </div>
                  );
                })()}
                {proposals.length === 0 ? (
                  <p className="text-muted small mb-0">
                    {(settings.tiposCampana || []).length === 0
                      ? 'Sin propuestas: configura primero los tipos de campaña arriba.'
                      : 'Sin propuestas todavía. Pulsa «Generar propuestas ahora» o programa la tarea diaria.'}
                  </p>
                ) : proposals.filter((p) => showCompletadas || p.status !== 'completada').map((p) => {
                  const abierta = expandedProposal === p.id;
                  const editable = ['propuesta', 'en_ajuste', 'error'].includes(p.status);
                  // Completada con campaña aún EN BORRADOR: el chat sigue vivo para pedir
                  // ajustes del EMAIL (el agente actualiza el borrador de /email-marketing).
                  const emailAjustable = p.status === 'completada' && p.campaign_status === 'borrador';
                  const locs = Array.isArray(p.locations) ? p.locations : [];
                  return (
                    <div className="border rounded p-2 mb-2" key={p.id}>
                      <div className="d-flex flex-wrap align-items-center gap-2" style={{ cursor: 'pointer' }} onClick={() => setExpandedProposal(abierta ? null : p.id)}>
                        <span className={`badge ${p.origen === 'reenganche' ? 'bg-info text-dark' : 'bg-primary'}`}>
                          {p.origen === 'reenganche' ? 'Reenganche' : 'Nueva audiencia'}
                        </span>
                        <strong className="small">{p.titulo}</strong>
                        {/* Los tipos de campaña admiten "{sector}" como placeholder: aquí se muestra resuelto */}
                        <span className="small text-muted">{p.sector}{locs.length ? ` · ${locs.join(', ')}` : ''}{p.tipo_campana_titulo ? ` · ${String(p.tipo_campana_titulo).replace(/\{\s*sector\s*\}/gi, p.sector || 'tu sector')}` : ''}</span>
                        <span className={`badge ms-auto ${
                          p.status === 'completada' ? 'bg-success' : p.status === 'ejecutando' ? 'bg-info text-dark' :
                          p.status === 'error' ? 'bg-danger' : p.status === 'en_ajuste' ? 'bg-warning text-dark' : 'bg-secondary'
                        }`}>{p.status === 'propuesta' ? 'nueva' : p.status}</span>
                        {/* En las completadas, estado real del email marketing (sin abrir la propuesta) */}
                        {p.status === 'completada' && p.campaign_status && (
                          <span className={`badge ${
                            p.campaign_status === 'enviada' ? 'bg-success' : p.campaign_status === 'programada' ? 'bg-info text-dark' :
                            p.campaign_status === 'cancelada' ? 'bg-secondary' : 'bg-warning text-dark'
                          }`} title="Estado de la campaña de email vinculada">
                            {p.campaign_status === 'enviada' ? '✓ email enviado' : p.campaign_status === 'programada' ? 'email programado' : p.campaign_status === 'cancelada' ? 'email cancelado' : 'email en borrador'}
                          </span>
                        )}
                        {p.status === 'completada' && p.campaign_status === 'enviada' && Number(p.enviados) > 0 && (
                          <span className="small text-muted" title="Resultados reales de la campaña (el agente aprende de ellos)">
                            {p.enviados} envíos · {Math.round((1000 * (p.abiertos || 0)) / p.enviados) / 10}% aperturas · {Math.round((1000 * (p.clics || 0)) / p.enviados) / 10}% clics
                            {Number(p.respuestas) > 0 ? ` · ${p.respuestas} respuestas` : ''}{Number(p.conversiones) > 0 ? ` · ${p.conversiones} conversiones` : ''}
                          </span>
                        )}
                        {p.status !== 'ejecutando' && (
                          <button
                            className="btn btn-sm btn-link text-danger p-0"
                            title={p.status === 'completada' ? 'Ocultar del listado' : 'Descartar propuesta'}
                            onClick={(e) => { e.stopPropagation(); discardProposal(p); }}
                          ><BsTrash size={14} /></button>
                        )}
                      </div>
                      {abierta && (
                        <div className="mt-2 small">
                          {p.razonamiento && <p className="mb-1"><strong>Por qué:</strong> {p.razonamiento}</p>}
                          {p.resumen_email && <p className="mb-1"><strong>Enfoque del email:</strong> {p.resumen_email}</p>}
                          {/* Email de la campaña: previsualizable SIEMPRE (antes de aprobar se
                              redacta y cachea: lo aprobado = lo previsualizado; después muestra
                              el HTML real del borrador de /email-marketing). */}
                          <div className="border rounded bg-light p-2 mb-2">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                              <strong><BsEnvelope className="me-1" size={12} />Email de la campaña</strong>
                              <div className="ms-auto d-flex gap-2">
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => previewEmail(p)} disabled={landingBusy[`em-${p.id}`]}>
                                  {landingBusy[`em-${p.id}`] ? <span className="spinner-border spinner-border-sm" /> : <><BsEye className="me-1" size={12} />Previsualizar email</>}
                                </button>
                              </div>
                            </div>
                            <div className="text-muted small mt-1">
                              {editable
                                ? 'Si aún no está redactado, el agente lo escribe al previsualizar (unos segundos) y lo que apruebes será exactamente esto. Pídele cambios por el chat de abajo.'
                                : emailAjustable
                                  ? 'La campaña sigue EN BORRADOR: puedes pedirle cambios al agente por el chat de abajo (asunto, tono, casos…) y actualizará el borrador.'
                                  : p.campaign_status
                                    ? 'El email ya no está en borrador: no se puede modificar desde aquí.'
                                    : 'Aún no hay campaña vinculada.'}
                            </div>
                          </div>
                          {(p.landing_content || p.landing_slug) && (
                            <div className="border rounded bg-light p-2 mb-2">
                              <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                <strong><BsGlobe className="me-1" size={12} />Landing</strong>
                                {p.landing_content?.titular && <span className="text-muted small">{p.landing_content.titular}{p.landing_content.oferta ? ` · ${p.landing_content.oferta}` : ''}</span>}
                                <span className={`badge ms-auto ${
                                  p.landing_status === 'publicada' ? 'bg-success' : p.landing_status === 'error' ? 'bg-danger' :
                                  p.landing_status === 'eliminada' ? 'bg-secondary' : 'bg-light text-dark border'
                                }`}>{p.landing_status === 'publicada' ? 'publicada' : p.landing_status === 'eliminada' ? 'retirada (30 días)' : p.landing_status === 'error' ? 'error' : 'sin publicar'}</span>
                              </div>
                              <div className="d-flex flex-wrap gap-2">
                                <button className="btn btn-sm btn-outline-secondary" onClick={() => previewLanding(p)} disabled={landingBusy[p.id]}>
                                  {landingBusy[p.id] ? <span className="spinner-border spinner-border-sm" /> : <><BsEye className="me-1" size={12} />Previsualizar</>}
                                </button>
                                {p.landing_url && (
                                  <a className="btn btn-sm btn-outline-primary" href={p.landing_url} target="_blank" rel="noopener noreferrer">
                                    <BsBoxArrowUpRight className="me-1" size={12} />Ver publicada
                                  </a>
                                )}
                                {/* También en propuestas ya COMPLETADAS cuya landing falló o quedó pendiente
                                    (p.ej. permisos del servidor): la campaña existe, solo falta la landing. */}
                                {sftpStatus?.configured && !['publicada', 'eliminada'].includes(p.landing_status) && p.status !== 'ejecutando' && (
                                  <button className="btn btn-sm btn-outline-success" onClick={() => publishLanding(p)} disabled={landingBusy[p.id]}>
                                    {landingBusy[p.id] ? <span className="spinner-border spinner-border-sm" /> : <><BsGlobe className="me-1" size={12} />Publicar landing</>}
                                  </button>
                                )}
                                {/* Republicar: re-genera el HTML con el builder ACTUAL y sobrescribe la
                                    landing online (útil para aplicar mejoras de plantilla a las ya publicadas) */}
                                {sftpStatus?.configured && p.landing_status === 'publicada' && (
                                  <button className="btn btn-sm btn-outline-success" title="Vuelve a generar y subir la landing con la plantilla más reciente" onClick={() => publishLanding(p)} disabled={landingBusy[p.id]}>
                                    {landingBusy[p.id] ? <span className="spinner-border spinner-border-sm" /> : 'Republicar'}
                                  </button>
                                )}
                              </div>
                              {!sftpStatus?.configured && (
                                <div className="text-muted small mt-1">La publicación automática está desactivada hasta configurar el SFTP (botón «Publicación de landings» arriba).</div>
                              )}
                            </div>
                          )}
                          {p.status === 'error' && p.error && <div className="alert alert-danger py-1 small mb-2">{p.error}</div>}
                          {p.status === 'completada' && p.result && (() => {
                            const dest = p.result.campana?.destinatarios || p.result.destinatarios;
                            const cs = p.campaign_status; // estado REAL de la campaña de email vinculada
                            const fecha = (d) => d ? new Date(d).toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '';
                            const map = {
                              enviada: { cls: 'alert-success', txt: <>Email marketing <strong>ENVIADO</strong>{p.campaign_updated_at ? ` el ${fecha(p.campaign_updated_at)}` : ''}.</> },
                              programada: { cls: 'alert-info', txt: <>Email marketing <strong>programado</strong>{p.campaign_scheduled_at ? ` para el ${fecha(p.campaign_scheduled_at)}` : ''}.</> },
                              cancelada: { cls: 'alert-secondary', txt: <>La campaña de email fue <strong>cancelada</strong>.</> },
                              borrador: { cls: 'alert-warning', txt: <>Email marketing <strong>EN BORRADOR</strong>{dest ? ` (${dest} destinatarios)` : ''}: aún no se ha enviado.</> }
                            };
                            const info = map[cs] || { cls: 'alert-secondary', txt: 'Campaña creada.' };
                            return (
                              <div className={`alert ${info.cls} py-1 small mb-2 d-flex flex-wrap align-items-center gap-2`}>
                                <span>{info.txt}</span>
                                <Link className="ms-auto" to="/email-marketing">Ver en /email-marketing →</Link>
                              </div>
                            );
                          })()}
                          {Array.isArray(p.chat) && p.chat.length > 0 && (
                            <div className="bg-light border rounded p-2 mb-2" style={{ maxHeight: 180, overflowY: 'auto' }}>
                              {p.chat.map((m, idx) => (
                                <div key={idx} className="mb-1">
                                  <strong className={m.role === 'usuario' ? 'text-primary' : 'text-success'}>{m.role === 'usuario' ? 'Tú' : 'Agente'}:</strong> {m.text}
                                </div>
                              ))}
                            </div>
                          )}
                          {(editable || emailAjustable) && (
                            <div className="d-flex gap-2 mb-2">
                              <input
                                className="form-control form-control-sm"
                                placeholder={emailAjustable
                                  ? 'Pide cambios del EMAIL: "acorta el asunto", "tono más directo", "quita el segundo caso"…'
                                  : 'Pídele ajustes: "cambia a Valencia y Alicante", "acorta el asunto del email"…'}
                                value={proposalChat[p.id] || ''}
                                onChange={(e) => setProposalChat((prev) => ({ ...prev, [p.id]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); sendProposalChat(p); } }}
                              />
                              <button className="btn btn-sm btn-outline-primary flex-shrink-0" onClick={() => sendProposalChat(p)} disabled={proposalBusy[p.id] || !(proposalChat[p.id] || '').trim()}>
                                {proposalBusy[p.id] ? <span className="spinner-border spinner-border-sm" /> : 'Pedir ajuste'}
                              </button>
                            </div>
                          )}
                          {editable && (
                            <div className="d-flex gap-2">
                              <button className="btn btn-sm btn-success" onClick={() => approveProposal(p)} disabled={proposalBusy[p.id]}>
                                Aprobar y ejecutar
                              </button>
                              <button className="btn btn-sm btn-outline-danger" onClick={() => discardProposal(p)} disabled={proposalBusy[p.id]}>
                                Descartar
                              </button>
                            </div>
                          )}
                          {p.status === 'ejecutando' && (
                            <div className="text-muted"><span className="spinner-border spinner-border-sm me-1" />El agente está captando leads y preparando la campaña…</div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ===== Agente Google Ads: campañas activas por cuenta (con fechas) ===== */}
          {tab === 'google_ads' && subtab === 'seguimiento' && (
            <div className="card agents-card mb-3">
              <div className="card-header d-flex flex-wrap justify-content-between align-items-center gap-2">
                <span className="d-flex align-items-center"><BsBarChartLine className="me-2 text-primary" />Campañas activas de las cuentas en seguimiento</span>
                <div className="d-flex align-items-center gap-2">
                  <input type="date" className="form-control form-control-sm gads-date" value={gadsRange.from}
                    onChange={(e) => setGadsRange((r) => ({ ...r, from: e.target.value }))} />
                  <span className="text-muted small">→</span>
                  <input type="date" className="form-control form-control-sm gads-date" value={gadsRange.to}
                    onChange={(e) => setGadsRange((r) => ({ ...r, to: e.target.value }))} />
                  <button className="btn btn-sm btn-outline-primary" onClick={fetchGadsCampaigns} disabled={gadsCampaignsLoading}>
                    {gadsCampaignsLoading ? <span className="spinner-border spinner-border-sm" /> : <BsArrowRepeat />}
                  </button>
                </div>
              </div>
              <div className="card-body">
                {gadsStatus && !gadsStatus.conectado && (
                  <div className="alert alert-warning py-2 small">
                    Falta conexión con la API: {!gadsStatus.developerToken && <strong>developer token</strong>}{!gadsStatus.developerToken && !gadsStatus.oauth && ' y '}{!gadsStatus.oauth && <strong>OAuth de Google</strong>}. Configúralos en <Link to="/google-ads">/google-ads → Configuración</Link>.
                  </div>
                )}
                {(settings.trackedAccounts || []).length === 0 && (
                  <div className="alert alert-info py-2 small d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <span><strong>No hay cuentas en seguimiento.</strong> Elige en «Configurar» qué cuentas (o subcuentas de tu MCC) quieres vigilar.</span>
                    <button className="btn btn-sm btn-primary flex-shrink-0" onClick={openSettings}><BsGear className="me-1" />Configurar</button>
                  </div>
                )}
                {gadsCampaignsLoading && !gadsCampaigns && <div className="text-center py-4"><div className="spinner-border" /></div>}
                {(gadsCampaigns?.accounts || []).map((acc) => (
                  <div className="border rounded p-2 mb-3" key={acc.id}>
                    <div className="d-flex align-items-center gap-2 mb-2">
                      <strong>{acc.name}</strong>
                      <span className="text-muted small">({acc.id})</span>
                      <span className="badge bg-light text-dark border ms-auto">{acc.campaigns.length} campaña(s) activa(s)</span>
                    </div>
                    {acc.error && <div className="alert alert-danger py-1 small mb-0">{acc.error}</div>}
                    {!acc.error && acc.campaigns.length === 0 && <p className="text-muted small mb-0">Sin campañas activas en el rango.</p>}
                    {acc.campaigns.length > 0 && (
                      <div className="table-responsive">
                        <table className="table table-sm align-middle mb-0">
                          <thead>
                            <tr className="small text-muted">
                              <th>Campaña</th><th>Tipo</th><th className="text-end">Optimización</th>
                              <th className="text-end">Impr.</th><th className="text-end">Clics</th><th className="text-end">CTR</th>
                              <th className="text-end">CPC</th><th className="text-end">Coste</th>
                              <th className="text-end">Conv.</th><th className="text-end">Coste/conv.</th>
                            </tr>
                          </thead>
                          <tbody>
                            {acc.campaigns.map((c) => {
                              const coste = (c.costMicros ?? 0) / 1e6;
                              const cpc = c.averageCpcMicros != null ? c.averageCpcMicros / 1e6 : null;
                              const opt = c.optimizationScore != null ? Math.round(c.optimizationScore * 1000) / 10 : null;
                              const umbral = settings.seguimiento?.umbral ?? 90;
                              return (
                                <tr key={c.campaignId}>
                                  <td className="small fw-semibold">{c.campaignName}</td>
                                  <td><span className="badge bg-light text-dark border">{(c.channelType || '').replace('PERFORMANCE_MAX', 'PMAX')}</span></td>
                                  <td className={`text-end fw-bold ${opt == null ? 'text-muted' : opt < umbral ? 'text-danger' : 'text-success'}`}>{opt == null ? '—' : `${opt}%`}</td>
                                  <td className="text-end">{(c.impressions ?? 0).toLocaleString('es-ES')}</td>
                                  <td className="text-end">{(c.clicks ?? 0).toLocaleString('es-ES')}</td>
                                  <td className="text-end">{c.ctr != null ? `${(c.ctr * 100).toFixed(2)}%` : '—'}</td>
                                  <td className="text-end">{cpc != null ? `${cpc.toFixed(2)}€` : '—'}</td>
                                  <td className="text-end">{coste.toFixed(2)}€</td>
                                  <td className="text-end">{(c.conversions ?? 0).toLocaleString('es-ES', { maximumFractionDigits: 1 })}</td>
                                  <td className="text-end">{c.conversions > 0 ? `${(coste / c.conversions).toFixed(2)}€` : '—'}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== Agente Google Ads: chat (consultas en vivo + propuestas) ===== */}
          {tab === 'google_ads' && subtab === 'chat-gads' && (
            <div className="card agents-card mb-3">
              <div className="card-header d-flex align-items-center">
                <BsRobot className="me-2 text-primary" />Chat con el agente
              </div>
              <div className="card-body">
                <p className="text-muted small mb-2">
                  Pregúntale lo que quieras de tus cuentas: consulta la API <strong>en vivo</strong> (no necesita datos guardados).
                  Ej.: «revisa las campañas activas de los últimos 3 meses y dime por qué hemos bajado conversiones, y dame una
                  propuesta de optimización». Las propuestas que genere van a la pestaña Propuestas y <strong>nada se aplica sin tu aprobación</strong>.
                </p>
                <div className="border rounded p-2 mb-2 bg-light" style={{ minHeight: 180, maxHeight: 420, overflowY: 'auto' }}>
                  {gadsChat.length === 0 && (
                    <p className="text-muted small mb-0">Sin conversación todavía. Escribe abajo tu primera consulta.</p>
                  )}
                  {gadsChat.map((m, i) => (
                    <div key={i} className={`mb-2 small ${m.role === 'usuario' ? 'text-end' : ''}`}>
                      <div className={`d-inline-block px-2 py-1 rounded ${m.role === 'usuario' ? 'bg-primary text-white' : 'bg-white border'}`}
                        style={{ maxWidth: '85%', whiteSpace: 'pre-wrap', textAlign: 'left' }}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  {gadsChatBusy && (
                    <div className="small text-muted"><span className="spinner-border spinner-border-sm me-1" />El agente está consultando la API…</div>
                  )}
                  <div ref={gadsChatEndRef}></div>
                </div>
                <div className="d-flex gap-2">
                  <textarea
                    className="form-control form-control-sm"
                    rows={2}
                    placeholder='Ej.: "¿por qué han bajado las conversiones de Mazuelas este trimestre?"'
                    value={gadsChatInput}
                    onChange={(e) => setGadsChatInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); sendGadsChat(); } }}
                  />
                  <button className="btn btn-primary flex-shrink-0" onClick={sendGadsChat} disabled={gadsChatBusy || !gadsChatInput.trim()}>
                    {gadsChatBusy ? <span className="spinner-border spinner-border-sm" /> : 'Enviar'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* ===== Agente Google Ads: análisis IA guardados ===== */}
          {tab === 'google_ads' && subtab === 'analisis' && (
            <div className="card agents-card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="d-flex align-items-center"><BsJournalText className="me-2 text-info" />Análisis diarios y globales</span>
                <button className="btn btn-sm btn-outline-secondary" onClick={fetchGadsAnalysis}><BsArrowRepeat /></button>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-2">
                  Con la «revisión de campañas» activada, el agente guarda cada día las métricas del día anterior, las analiza según el
                  tipo de campaña y añade un análisis <strong>global</strong> construido sobre sus análisis previos. Retención: {settings.revision?.retencionDias ?? 90} días.
                </p>
                {gadsAnalysis.length === 0 ? (
                  <p className="text-muted small mb-0">Sin análisis todavía. Actívalo en «Configurar → Revisión de campañas» o pulsa «Ejecutar ahora» en Tareas.</p>
                ) : gadsAnalysis.map((a) => (
                  <div className="border rounded p-2 mb-2" key={a.id}>
                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                      <strong className="small">{a.campaign_name}</strong>
                      <span className="badge bg-light text-dark border">{(a.channel_type || '').replace('PERFORMANCE_MAX', 'PMAX')}</span>
                      <span className="text-muted small ms-auto">{new Date(a.date).toLocaleDateString('es-ES')}</span>
                    </div>
                    <p className="small mb-1"><strong>Últimas 24h:</strong> {a.analysis_daily}</p>
                    {a.analysis_global && <p className="small mb-1"><strong>Global (histórico):</strong> {a.analysis_global}</p>}
                    {Array.isArray(a.recommendations) && a.recommendations.length > 0 && (
                      <div className="d-flex flex-wrap gap-1">
                        {a.recommendations.map((r, i) => (
                          <span key={i} className="badge bg-warning-subtle text-dark border" title={r.texto}>{r.tipo}: {String(r.texto || '').slice(0, 70)}{String(r.texto || '').length > 70 ? '…' : ''}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ===== Agente Google Ads: propuestas de optimización (aprobación humana) ===== */}
          {tab === 'google_ads' && subtab === 'propuestas-gads' && (
            <div className="card agents-card mb-3">
              <div className="card-header d-flex justify-content-between align-items-center">
                <span className="d-flex align-items-center"><BsLightbulb className="me-2 text-warning" />Propuestas de optimización
                  {gadsProposals.filter((p) => p.status === 'propuesta').length > 0 && (
                    <span className="badge bg-primary ms-2">{gadsProposals.filter((p) => p.status === 'propuesta').length}</span>
                  )}
                </span>
                <button className="btn btn-sm btn-outline-secondary" onClick={fetchGadsProposals}><BsArrowRepeat /></button>
              </div>
              <div className="card-body">
                <p className="text-muted small mb-2">
                  Mejoras concretas que el agente propone (p.ej. negativizar términos caros sin conversiones), revisadas por el
                  verificador. <strong>Nada se aplica en Google Ads sin tu aprobación.</strong>
                </p>
                {gadsProposals.length === 0 ? (
                  <p className="text-muted small mb-0">Sin propuestas. Activa la «optimización» en Configurar (diaria o semanal).</p>
                ) : gadsProposals.map((p) => {
                  const verif = p.verificacion || null;
                  return (
                    <div className="border rounded p-2 mb-2" key={p.id}>
                      <div className="d-flex flex-wrap align-items-center gap-2">
                        <strong className="small">{p.titulo}</strong>
                        <span className="text-muted small">{p.campaign_name} · {p.customer_name || p.customer_id}</span>
                        <span className={`badge ms-auto ${
                          p.status === 'ejecutada' ? 'bg-success' : p.status === 'error' ? 'bg-danger' : 'bg-secondary'
                        }`}>{p.status === 'propuesta' ? 'pendiente' : p.status}</span>
                      </div>
                      <p className="small text-muted mb-1 mt-1">{p.detalle}</p>
                      {verif && (
                        <div className="small mb-1">
                          <span className={`badge me-1 ${verif.riesgo === 'alto' ? 'bg-danger' : verif.riesgo === 'medio' ? 'bg-warning text-dark' : verif.riesgo === 'bajo' ? 'bg-success' : 'bg-secondary'}`}>
                            Verificador: riesgo {verif.riesgo || 'n/d'}
                          </span>
                          {Array.isArray(verif.avisos) && verif.avisos.map((av, i) => <span key={i} className="text-muted">· {av} </span>)}
                        </div>
                      )}
                      {p.status === 'error' && p.error && <div className="alert alert-danger py-1 small mb-1">{p.error}</div>}
                      {p.status === 'propuesta' && (
                        <div className="d-flex gap-2 mt-1">
                          <button className="btn btn-sm btn-success" onClick={() => approveGadsProposal(p)} disabled={gadsBusy[p.id]}>
                            {gadsBusy[p.id] ? <span className="spinner-border spinner-border-sm" /> : 'Aprobar y aplicar'}
                          </button>
                          <button className="btn btn-sm btn-outline-danger" onClick={() => discardGadsProposal(p)} disabled={gadsBusy[p.id]}>Descartar</button>
                        </div>
                      )}
                      {p.status === 'error' && (
                        <button className="btn btn-sm btn-outline-secondary mt-1" onClick={() => discardGadsProposal(p)} disabled={gadsBusy[p.id]}>Quitar del listado</button>
                      )}
                      {p.status === 'ejecutada' && (
                        <div className="d-flex flex-wrap align-items-center gap-2 small">
                          {p.executed_at && <span className="text-muted">Aplicada el {new Date(p.executed_at).toLocaleString('es-ES')}</span>}
                          {p.post_check ? (
                            <span
                              className={`badge ${p.post_check.ok ? 'bg-success' : 'bg-danger'}`}
                              title={(p.post_check.comprobaciones || []).map((c) => `${c.ok ? '✓' : '✗'} ${c.detalle}`).join('\n')}
                            >
                              {p.post_check.ok ? 'Cambio verificado en Google Ads ✓' : 'Verificación post-cambio FALLÓ'}
                            </span>
                          ) : (
                            <span className="badge bg-light text-dark border">verificación pendiente…</span>
                          )}
                          <button className="btn btn-sm btn-link p-0 text-decoration-none" onClick={() => recheckGadsProposal(p)} disabled={gadsBusy[p.id]}>
                            Re-verificar
                          </button>
                        </div>
                      )}
                      {p.status === 'ejecutada' && p.post_check && !p.post_check.ok && (
                        <div className="alert alert-danger py-1 small mt-1 mb-0">
                          {(p.post_check.comprobaciones || []).filter((c) => !c.ok).map((c, i) => <div key={i}>✗ {c.detalle}</div>)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {subtab === 'memoria' && tab !== 'google_ads' && (
          <div className="card agents-card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span className="d-flex align-items-center"><BsJournalText className="me-2 text-info" />Memoria del agente {memoryNotes.length > 0 && <span className="badge bg-secondary ms-1">{memoryNotes.length}</span>}</span>
            </div>
              <div className="card-body">
                <p className="text-muted small mb-2">
                  Aprendizajes operativos que el agente extrae de tus aprobaciones («prefiere WhatsApp», «no insistir hasta
                  septiembre») y relee antes de redactar. Las notas por sector guían las campañas de captación. Puedes añadir,
                  editar o borrar lo que quieras: el agente respeta lo que haya aquí.
                </p>
                <div className="border rounded p-2 mb-3">
                  <div className="row g-2 align-items-end">
                    <div className="col-md-2">
                      <label className="form-label small mb-0">Ámbito</label>
                      <select className="form-select form-select-sm" value={memoryForm.scope} onChange={(e) => setMemoryForm((p) => ({ ...p, scope: e.target.value }))}>
                        <option value="cliente">Cliente</option>
                        <option value="sector">Sector</option>
                        <option value="captacion">Captación (regla general)</option>
                      </select>
                    </div>
                    {memoryForm.scope === 'cliente' ? (
                      <div className="col-md-3" style={{ position: 'relative' }}>
                        <label className="form-label small mb-0">Cliente</label>
                        <input
                          className="form-control form-control-sm"
                          placeholder="Busca por nombre…"
                          value={memoryForm.clientQuery}
                          onChange={(e) => searchMemoryClients(e.target.value)}
                        />
                        {memoryClientOptions.length > 0 && !memoryForm.clientId && (
                          <div className="list-group shadow-sm" style={{ position: 'absolute', zIndex: 20, width: '100%' }}>
                            {memoryClientOptions.map((c) => (
                              <button key={c.id} type="button" className="list-group-item list-group-item-action py-1 small"
                                onClick={() => { setMemoryForm((p) => ({ ...p, clientId: c.id, clientQuery: c.nombre })); setMemoryClientOptions([]); }}>
                                {c.nombre}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div className="col-md-3">
                        <label className="form-label small mb-0">Sector</label>
                        <input className="form-control form-control-sm" placeholder="ej. peluquería" value={memoryForm.sector} onChange={(e) => setMemoryForm((p) => ({ ...p, sector: e.target.value }))} />
                      </div>
                    )}
                    <div className="col-md-5">
                      <label className="form-label small mb-0">Nota</label>
                      <input className="form-control form-control-sm" placeholder='ej. "prefiere que le llamen por las tardes"' value={memoryForm.nota} onChange={(e) => setMemoryForm((p) => ({ ...p, nota: e.target.value }))} />
                    </div>
                    <div className="col-md-2">
                      <button className="btn btn-sm btn-primary w-100" onClick={saveMemoryNote} disabled={memorySaving || !memoryForm.nota.trim()}>
                        {memorySaving ? 'Guardando…' : 'Añadir nota'}
                      </button>
                    </div>
                  </div>
                </div>
                <input className="form-control form-control-sm mb-2" placeholder="Filtrar notas…" value={memoryFilter} onChange={(e) => setMemoryFilter(e.target.value)} />
                {memoryNotes.length === 0 ? (
                  <p className="text-muted small mb-0">Sin notas todavía: se irán creando solas con tus aprobaciones, o añade la primera.</p>
                ) : (
                  <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                    {memoryNotes
                      .filter((n) => {
                        const f = memoryFilter.trim().toLowerCase();
                        if (!f) return true;
                        return `${n.client_name || ''} ${n.sector || ''} ${n.nota || ''}`.toLowerCase().includes(f);
                      })
                      .map((n) => (
                        <div className="d-flex align-items-start gap-2 border-bottom py-1 small" key={n.id}>
                          <span className={`badge flex-shrink-0 ${n.scope === 'sector' ? 'bg-info text-dark' : n.scope === 'captacion' ? 'bg-warning text-dark' : 'bg-primary'}`}>
                            {n.scope === 'sector' ? `Sector: ${n.sector}` : n.scope === 'captacion' ? 'Captación' : (n.client_name || `Cliente #${n.client_id}`)}
                          </span>
                          {memoryEditing?.id === n.id ? (
                            <>
                              <input className="form-control form-control-sm" value={memoryEditing.nota} onChange={(e) => setMemoryEditing((p) => ({ ...p, nota: e.target.value }))} />
                              <button className="btn btn-sm btn-success py-0 flex-shrink-0" onClick={saveMemoryEdit}>OK</button>
                              <button className="btn btn-sm btn-outline-secondary py-0 flex-shrink-0" onClick={() => setMemoryEditing(null)}>✕</button>
                            </>
                          ) : (
                            <>
                              <span className="flex-grow-1">{n.nota}</span>
                              <span className="text-muted flex-shrink-0" title={n.origen === 'agente' ? 'Aprendida por el agente' : 'Escrita por ti'}>
                                {n.origen === 'agente' ? '🤖' : '👤'} {formatDateTime(n.created_at)}
                              </span>
                              <button className="btn btn-sm btn-outline-secondary py-0 flex-shrink-0" title="Editar" onClick={() => setMemoryEditing({ id: n.id, nota: n.nota })}><BsPencil size={11} /></button>
                              <button className="btn btn-sm btn-outline-danger py-0 flex-shrink-0" title="Borrar" onClick={() => deleteMemoryNote(n)}><BsTrash size={11} /></button>
                            </>
                          )}
                        </div>
                      ))}
                  </div>
                )}
              </div>
          </div>
          )}

          {subtab === 'tareas' && (
          <div className="card agents-card mb-3">
            <div className="card-header d-flex justify-content-between align-items-center">
              <span className="d-flex align-items-center"><BsCalendar2Check className="me-2 text-primary" />Tareas programadas</span>
              <button className="btn btn-sm btn-primary d-flex align-items-center" onClick={openNew}>
                <BsPlusLg className="me-1" /> Nueva tarea
              </button>
            </div>
            <div className="card-body p-0">
              {tasks.length === 0 ? (
                <p className="text-muted small m-3">Sin tareas programadas. Crea una, por ejemplo: {tab === 'comercial' ? '"todos los días a las 9:00 revisa las comunicaciones derivadas"' : '"cada lunes capta restaurantes en Murcia"'}.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Título</th>
                        <th>Programación</th>
                        <th>Próxima ejecución</th>
                        <th>Última</th>
                        <th>Activa</th>
                        <th className="text-end">Acciones</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tasks.map((task) => (
                        <tr key={task.id}>
                          <td>
                            {task.titulo}
                            {isTaskRunning(task) && <span className="badge bg-info text-dark ms-2">En ejecución</span>}
                            {task.instrucciones && <div className="small text-muted text-truncate" style={{ maxWidth: 320 }}>{task.instrucciones}</div>}
                          </td>
                          <td>{scheduleLabel(task)}</td>
                          <td>{task.schedule_type === 'manual' ? '-' : formatDateTime(task.next_run_at)}</td>
                          <td>{formatDateTime(task.last_run_at)}</td>
                          <td>
                            <div className="form-check form-switch">
                              <input className="form-check-input" type="checkbox" checked={task.enabled !== false} onChange={() => toggleEnabled(task)} />
                            </div>
                          </td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-success me-1" title="Ejecutar ahora" disabled={runningTaskId === task.id || isTaskRunning(task)} onClick={() => runNow(task)}>
                              <BsPlayFill />
                            </button>
                            <button className="btn btn-sm btn-outline-secondary me-1" title="Editar" onClick={() => openEdit(task)}>
                              <BsPencil />
                            </button>
                            <button className="btn btn-sm btn-outline-danger" title="Eliminar" onClick={() => removeTask(task)}>
                              <BsTrash />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          )}

          {subtab === 'historial' && (
          <div className="card agents-card">
            <div className="card-header d-flex align-items-center"><BsClockHistory className="me-2 text-secondary" />Historial de ejecuciones</div>
            <div className="card-body p-0">
              {logs.length === 0 ? (
                <p className="text-muted small m-3">Todavía no hay ejecuciones.</p>
              ) : (
                <div className="table-responsive">
                  <table className="table table-striped table-sm align-middle mb-0">
                    <thead>
                      <tr>
                        <th>Inicio</th>
                        <th>Tarea</th>
                        <th>Disparo</th>
                        <th>Estado</th>
                        <th>Resumen</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {logs.map((log) => (
                        <tr key={log.id}>
                          <td>{formatDateTime(log.started_at)}</td>
                          <td className="small">{log.task_titulo || 'Orden puntual'}</td>
                          <td className="small">{log.trigger_type === 'schedule' ? 'Programado' : 'Manual'}</td>
                          <td><StatusBadge status={log.status} /></td>
                          <td className="small">{log.summary || (log.status === 'running' ? 'En curso…' : log.error || '-')}</td>
                          <td className="text-end">
                            <button className="btn btn-sm btn-outline-secondary" onClick={() => openLogDetail(log)}>Detalle</button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
          )}
        </>
      )}

      {modalOpen && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }} onClick={(e) => { if (e.target === e.currentTarget) setModalOpen(false); }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <form onSubmit={submitTask}>
                <div className="modal-header">
                  <h5 className="modal-title">{form.id ? 'Editar tarea' : 'Nueva tarea'} · {tab === 'comercial' ? 'Agente comercial' : tab === 'google_ads' ? 'Agente Google Ads' : 'Agente de captación'}</h5>
                  <button type="button" className="btn-close" onClick={() => setModalOpen(false)}></button>
                </div>
                <div className="modal-body">
                  <div className="mb-2">
                    <label className="form-label">Título</label>
                    <input className="form-control" value={form.titulo} onChange={(e) => setForm((p) => ({ ...p, titulo: e.target.value }))} required />
                  </div>
                  <div className="mb-2">
                    <label className="form-label">Instrucciones {tab === 'captacion' ? '(qué captar y dónde)' : '(opcional: matices para el agente)'}</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      value={form.instrucciones}
                      onChange={(e) => setForm((p) => ({ ...p, instrucciones: e.target.value }))}
                      placeholder={tab === 'captacion' ? 'Ej.: capta peluquerías en Madrid y alrededores' : 'Ej.: prioriza presupuestos con servicios del catálogo; tono cercano'}
                      required={tab === 'captacion'}
                    />
                  </div>
                  <div className="row g-2 mb-2">
                    <div className="col-6">
                      <label className="form-label">Frecuencia</label>
                      <select className="form-select" value={form.scheduleType} onChange={(e) => setForm((p) => ({ ...p, scheduleType: e.target.value }))}>
                        <option value="diaria">Diaria</option>
                        <option value="semanal">Semanal</option>
                        <option value="intervalo">Cada X minutos</option>
                        <option value="manual">Solo manual</option>
                      </select>
                    </div>
                    {(form.scheduleType === 'diaria' || form.scheduleType === 'semanal') && (
                      <div className="col-6">
                        <label className="form-label">Hora</label>
                        <input className="form-control" type="time" value={form.runAtTime} onChange={(e) => setForm((p) => ({ ...p, runAtTime: e.target.value }))} />
                      </div>
                    )}
                    {form.scheduleType === 'semanal' && (
                      <div className="col-6">
                        <label className="form-label">Día de la semana</label>
                        <select className="form-select" value={form.weekday} onChange={(e) => setForm((p) => ({ ...p, weekday: Number(e.target.value) }))}>
                          {WEEKDAYS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                        </select>
                      </div>
                    )}
                    {form.scheduleType === 'intervalo' && (
                      <div className="col-6">
                        <label className="form-label">Intervalo (minutos)</label>
                        <input className="form-control" type="number" min="5" value={form.intervalMinutes} onChange={(e) => setForm((p) => ({ ...p, intervalMinutes: Number(e.target.value) }))} />
                      </div>
                    )}
                  </div>
                  <div className="form-check">
                    <input className="form-check-input" type="checkbox" id="taskEnabled" checked={form.enabled} onChange={(e) => setForm((p) => ({ ...p, enabled: e.target.checked }))} />
                    <label className="form-check-label" htmlFor="taskEnabled">Activa</label>
                  </div>
                </div>
                <div className="modal-footer">
                  <button type="button" className="btn btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={saving}>
                    {saving ? 'Guardando…' : 'Guardar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {settingsOpen && settingsDraft && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }} onClick={(e) => { if (e.target === e.currentTarget) setSettingsOpen(false); }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  <BsGear className="me-2" />Configurar · {tab === 'comercial' ? 'Agente comercial' : tab === 'google_ads' ? 'Agente Google Ads' : 'Agente de captación'}
                </h5>
                <button className="btn-close" onClick={() => setSettingsOpen(false)}></button>
              </div>
              <div className="modal-body">
                {proveedores && (
                  <div className="border rounded p-2 mb-3 bg-light">
                    <label className="form-label fw-bold mb-1">Modelo de IA del agente</label>
                    <div className="row g-2">
                      <div className="col-6">
                        <select
                          className="form-select form-select-sm"
                          value={settingsDraft.provider || 'openai'}
                          onChange={(e) => {
                            const provider = e.target.value;
                            const modelos = proveedores[provider]?.modelos || [];
                            setSettingsDraft((p) => ({ ...p, provider, model: modelos[0] || '' }));
                          }}
                        >
                          {Object.entries(proveedores).map(([key, prov]) => (
                            <option key={key} value={key} disabled={key === 'claude' && !claudeDisponible}>
                              {prov.nombre}{key === 'claude' && !claudeDisponible ? ' (falta ANTHROPIC_API_KEY)' : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="col-6">
                        <select
                          className="form-select form-select-sm"
                          value={settingsDraft.model || ''}
                          onChange={(e) => setSettingsDraft((p) => ({ ...p, model: e.target.value }))}
                        >
                          <option value="">
                            {(() => {
                              const prov = settingsDraft.provider || 'openai';
                              const def = prov === 'openai'
                                ? (currentAgent?.modeloDefault || (proveedores[prov]?.modelos || [])[0])
                                : (proveedores[prov]?.modelos || [])[0];
                              return `Modelo por defecto${def ? ` (${def})` : ''}`;
                            })()}
                          </option>
                          {(proveedores[settingsDraft.provider || 'openai']?.modelos || []).map((m) => (
                            <option key={m} value={m}>{m}</option>
                          ))}
                        </select>
                      </div>
                    </div>
                    <div className="form-text">
                      Con qué "cerebro" piensa este agente. Para usar Claude hay que añadir la clave
                      ANTHROPIC_API_KEY en las variables del servidor (DigitalOcean).
                    </div>
                  </div>
                )}
                {tab === 'comercial' && (
                  <>
                    <label className="form-label fw-bold">Indicaciones para la creación de presupuestos</label>
                    <textarea
                      className="form-control"
                      rows="7"
                      value={settingsDraft.indicacionesPresupuestos || ''}
                      onChange={(e) => setSettingsDraft((p) => ({ ...p, indicacionesPresupuestos: e.target.value }))}
                      placeholder={'Ej.: desarrollo de módulos a medida Dolibarr: tarifas entre 600 y 3000€, nunca más caro; los presupuestos más caros para los de más complejidad. Rediseño Prestashop y corrección de errores: entre 1100 y 3200€ según complejidad, no más caro.'}
                    />
                    <div className="form-text">
                      El agente aplica estas indicaciones como reglas OBLIGATORIAS al poner precios, junto con el catálogo de
                      /servicios y los últimos 20 presupuestos de la empresa. El revisor automático avisa si un presupuesto se sale de ellas.
                    </div>
                  </>
                )}
                {tab === 'captacion' && (
                  <>
                    <label className="form-label fw-bold">Tipos de campaña de email marketing</label>
                    <p className="small text-muted mb-2">
                      Define aquí los tipos de campaña que el agente puede preparar para los leads captados (siempre quedan en
                      borrador en /email-marketing). Ejemplo: título «diseño web», descripción «diseñar o renovar página web desde
                      250€ con optimización SEO y de rendimiento».
                    </p>
                    {(settingsDraft.tiposCampana || []).map((tipo, idx) => (
                      <div className="border rounded p-2 mb-2" key={tipo.id || `nuevo-${idx}`}>
                        <div className="d-flex gap-2 mb-1">
                          <input
                            className="form-control form-control-sm"
                            placeholder="Título (ej. diseño web)"
                            value={tipo.titulo || ''}
                            onChange={(e) => setSettingsDraft((p) => {
                              const tipos = [...p.tiposCampana];
                              tipos[idx] = { ...tipos[idx], titulo: e.target.value };
                              return { ...p, tiposCampana: tipos };
                            })}
                          />
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            title="Eliminar tipo"
                            onClick={() => setSettingsDraft((p) => ({ ...p, tiposCampana: p.tiposCampana.filter((_t, i) => i !== idx) }))}
                          ><BsTrash /></button>
                        </div>
                        <textarea
                          className="form-control form-control-sm"
                          rows="2"
                          placeholder="Descripción / oferta (ej. diseñar o renovar página web desde 250€ con optimización SEO...)"
                          value={tipo.descripcion || ''}
                          onChange={(e) => setSettingsDraft((p) => {
                            const tipos = [...p.tiposCampana];
                            tipos[idx] = { ...tipos[idx], descripcion: e.target.value };
                            return { ...p, tiposCampana: tipos };
                          })}
                        />
                      </div>
                    ))}
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary d-flex align-items-center"
                      onClick={() => setSettingsDraft((p) => ({
                        ...p,
                        tiposCampana: [...(p.tiposCampana || []), { id: Date.now(), titulo: '', descripcion: '' }]
                      }))}
                    ><BsPlusLg className="me-1" />Añadir tipo de campaña</button>
                  </>
                )}
                {tab === 'google_ads' && settingsDraft.seguimiento && (
                  <>
                    {/* Cuentas a vigilar (de las accesibles con tu OAuth, incluye subcuentas del MCC) */}
                    <label className="form-label fw-bold mb-1">Cuentas en seguimiento</label>
                    <p className="small text-muted mb-2">Marca las cuentas (o subcuentas de tu MCC) cuyas campañas activas quieres vigilar.</p>
                    <div className="border rounded p-2 mb-3" style={{ maxHeight: 220, overflowY: 'auto' }}>
                      {gadsAccountsLoading && <div className="text-muted small"><span className="spinner-border spinner-border-sm me-1" />Cargando cuentas…</div>}
                      {!gadsAccountsLoading && gadsAccounts.length === 0 && (
                        <div className="text-muted small">
                          No se pudieron cargar las cuentas. Revisa la conexión en <Link to="/google-ads">/google-ads</Link> y
                          <button className="btn btn-link btn-sm p-0 ms-1" onClick={fetchGadsAccounts}>reintenta</button>.
                        </div>
                      )}
                      {gadsAccounts.map((acc) => {
                        const checked = (settingsDraft.trackedAccounts || []).some((a) => String(a.id) === String(acc.id));
                        return (
                          <label className="d-flex align-items-center gap-2 py-1 small" key={acc.id} style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked={checked}
                              disabled={acc.isManager}
                              onChange={(e) => setSettingsDraft((p) => ({
                                ...p,
                                trackedAccounts: e.target.checked
                                  ? [...(p.trackedAccounts || []), { id: acc.id, name: acc.name, loginCustomerId: acc.loginCustomerId || '' }]
                                  : (p.trackedAccounts || []).filter((a) => String(a.id) !== String(acc.id))
                              }))}
                            />
                            <span className="fw-semibold">{acc.name}</span>
                            <span className="text-muted">({acc.id})</span>
                            {acc.isManager && <span className="badge bg-info text-dark" title="Las cuentas MCC no tienen campañas propias: marca sus subcuentas">MCC</span>}
                          </label>
                        );
                      })}
                      {/* Cuentas en seguimiento que ya no aparecen entre las accesibles: se muestran para poder desmarcarlas */}
                      {(settingsDraft.trackedAccounts || [])
                        .filter((a) => !gadsAccounts.some((acc) => String(acc.id) === String(a.id)))
                        .map((a) => (
                          <label className="d-flex align-items-center gap-2 py-1 small" key={`missing-${a.id}`} style={{ cursor: 'pointer' }}>
                            <input
                              type="checkbox"
                              className="form-check-input mt-0"
                              checked
                              onChange={() => setSettingsDraft((p) => ({
                                ...p,
                                trackedAccounts: (p.trackedAccounts || []).filter((t) => String(t.id) !== String(a.id))
                              }))}
                            />
                            <span className="fw-semibold">{a.name || a.id}</span>
                            <span className="text-muted">({a.id})</span>
                            <span className="badge bg-warning text-dark" title="Esta cuenta ya no es accesible con tu login actual">no accesible</span>
                          </label>
                        ))}
                    </div>

                    {/* Seguimiento diario del % de optimización */}
                    <div className="border rounded p-2 mb-2">
                      <div className="form-check form-switch mb-1">
                        <input className="form-check-input" type="checkbox" id="gadsSeg" checked={settingsDraft.seguimiento.enabled === true}
                          onChange={(e) => setSettingsDraft((p) => ({ ...p, seguimiento: { ...p.seguimiento, enabled: e.target.checked } }))} />
                        <label className="form-check-label fw-bold" htmlFor="gadsSeg">Seguimiento diario del % de optimización</label>
                      </div>
                      <div className="row g-2 align-items-end">
                        <div className="col-6 col-md-4">
                          <label className="form-label small mb-0">Hora del ciclo diario</label>
                          <input type="time" className="form-control form-control-sm" value={settingsDraft.seguimiento.hora || '09:00'}
                            onChange={(e) => setSettingsDraft((p) => ({ ...p, seguimiento: { ...p.seguimiento, hora: e.target.value } }))} />
                        </div>
                        <div className="col-6 col-md-4">
                          <label className="form-label small mb-0">Avisar si baja del (%)</label>
                          <input type="number" min="1" max="100" className="form-control form-control-sm" value={settingsDraft.seguimiento.umbral ?? 90}
                            onChange={(e) => setSettingsDraft((p) => ({ ...p, seguimiento: { ...p.seguimiento, umbral: Number(e.target.value) } }))} />
                        </div>
                        <div className="col-12"><div className="form-text mt-0">Si alguna campaña activa baja del umbral recibirás una notificación push.</div></div>
                      </div>
                    </div>

                    {/* Revisión de campañas: recolección + análisis IA con retención */}
                    <div className="border rounded p-2 mb-2">
                      <div className="form-check form-switch mb-1">
                        <input className="form-check-input" type="checkbox" id="gadsRev" checked={settingsDraft.revision.enabled === true}
                          onChange={(e) => setSettingsDraft((p) => ({ ...p, revision: { ...p.revision, enabled: e.target.checked } }))} />
                        <label className="form-check-label fw-bold" htmlFor="gadsRev">Revisión de campañas (recolección + análisis IA)</label>
                      </div>
                      <div className="row g-2 align-items-end">
                        <div className="col-6 col-md-4">
                          <label className="form-label small mb-0">Retención de datos (días)</label>
                          <input type="number" min="7" max="730" className="form-control form-control-sm" value={settingsDraft.revision.retencionDias ?? 90}
                            onChange={(e) => setSettingsDraft((p) => ({ ...p, revision: { ...p.revision, retencionDias: Number(e.target.value) } }))} />
                        </div>
                        <div className="col-12">
                          <div className="form-text mt-0">
                            Cada día guarda las métricas del día anterior por campaña (según su tipo) y genera el análisis diario +
                            global con IA. Los datos más antiguos que la retención se borran solos.
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Optimización: propuestas con aprobación humana */}
                    <div className="border rounded p-2 mb-1">
                      <div className="form-check form-switch mb-1">
                        <input className="form-check-input" type="checkbox" id="gadsOpt" checked={settingsDraft.optimizacion.enabled === true}
                          onChange={(e) => setSettingsDraft((p) => ({ ...p, optimizacion: { ...p.optimizacion, enabled: e.target.checked } }))} />
                        <label className="form-check-label fw-bold" htmlFor="gadsOpt">Propuestas de optimización (con tu aprobación)</label>
                      </div>
                      <div className="row g-2 align-items-end">
                        <div className="col-6 col-md-4">
                          <label className="form-label small mb-0">Frecuencia</label>
                          <select className="form-select form-select-sm" value={settingsDraft.optimizacion.frecuencia || 'semanal'}
                            onChange={(e) => setSettingsDraft((p) => ({ ...p, optimizacion: { ...p.optimizacion, frecuencia: e.target.value } }))}>
                            <option value="semanal">Semanal (lunes)</option>
                            <option value="diaria">Diaria</option>
                          </select>
                        </div>
                        <div className="col-12">
                          <div className="form-text mt-0">
                            El agente propone mejoras (negativizar términos caros sin conversión…), el verificador las revisa y
                            <strong> nada se aplica en Google Ads sin que tú lo apruebes</strong>.
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary" onClick={() => setSettingsOpen(false)}>Cancelar</button>
                <button className="btn btn-primary" onClick={saveSettings} disabled={settingsSaving}>
                  {settingsSaving ? 'Guardando…' : 'Guardar configuración'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {capsOpen && currentAgent && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }} onClick={(e) => { if (e.target === e.currentTarget) setCapsOpen(false); }}>
          <div className="modal-dialog modal-lg modal-dialog-scrollable">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title d-flex align-items-center">
                  <BsRobot className="me-2" />{currentAgent.nombre} · capacidades
                </h5>
                <button className="btn-close" onClick={() => setCapsOpen(false)}></button>
              </div>
              <div className="modal-body">
                <p className="text-muted small">{currentAgent.descripcion}</p>
                {currentAgent.modelo && (
                  <p className="small mb-3">
                    <strong>Modelo de IA:</strong> <span className="badge bg-light text-dark border">{currentAgent.modelo}</span>
                  </p>
                )}

                {(currentAgent.capacidades?.herramientas || []).length > 0 && (
                  <>
                    <h6 className="d-flex align-items-center"><BsTools className="me-2" />Herramientas</h6>
                    <div className="list-group mb-3">
                      {currentAgent.capacidades.herramientas.map((h) => (
                        <div className="list-group-item" key={h.nombre}>
                          <div className="d-flex align-items-center gap-2 mb-1">
                            <strong className="small">{h.titulo}</strong>
                            <code className="small text-muted">{h.nombre}</code>
                          </div>
                          <div className="small text-muted">{h.descripcion}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {(currentAgent.capacidades?.subagentes || []).length > 0 && (
                  <>
                    <h6 className="d-flex align-items-center"><BsDiagram3 className="me-2" />Subagentes y servicios de apoyo</h6>
                    <div className="list-group mb-3">
                      {currentAgent.capacidades.subagentes.map((s) => (
                        <div className="list-group-item" key={s.nombre}>
                          <strong className="small d-block mb-1">{s.nombre}</strong>
                          <div className="small text-muted">{s.descripcion}</div>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                {(currentAgent.capacidades?.flujo || []).length > 0 && (
                  <>
                    <h6 className="d-flex align-items-center"><BsListOl className="me-2" />Cómo trabaja</h6>
                    <ol className="small mb-3">
                      {currentAgent.capacidades.flujo.map((paso, idx) => <li key={idx} className="mb-1">{paso}</li>)}
                    </ol>
                  </>
                )}

                {(currentAgent.capacidades?.fuentes || []).length > 0 && (
                  <>
                    <h6 className="d-flex align-items-center"><BsDatabase className="me-2" />Fuentes de contexto</h6>
                    <ul className="small mb-3">
                      {currentAgent.capacidades.fuentes.map((f, idx) => <li key={idx}>{f}</li>)}
                    </ul>
                  </>
                )}

                {(currentAgent.capacidades?.limites || []).length > 0 && (
                  <>
                    <h6 className="d-flex align-items-center"><BsShieldCheck className="me-2" />Límites y seguridad</h6>
                    <ul className="small mb-0">
                      {currentAgent.capacidades.limites.map((l, idx) => <li key={idx}>{l}</li>)}
                    </ul>
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn btn-secondary btn-sm" onClick={() => setCapsOpen(false)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {logDetail && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.35)' }} onClick={(e) => { if (e.target === e.currentTarget) setLogDetail(null); }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Ejecución #{logDetail.id} · <StatusBadge status={logDetail.status} /></h5>
                <button className="btn-close" onClick={() => setLogDetail(null)}></button>
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-2">
                  {logDetail.task_titulo || 'Orden puntual'} · {formatDateTime(logDetail.started_at)}
                  {logDetail.finished_at ? ` → ${formatDateTime(logDetail.finished_at)}` : ' (en curso)'}
                </p>
                {logDetail.summary && <p className="mb-2"><strong>{logDetail.summary}</strong></p>}
                {logDetail.error && <div className="alert alert-danger py-2 small">{logDetail.error}</div>}
                {renderLogDetail(logDetail.detail)}
              </div>
              <div className="modal-footer">
                <button className="btn btn-outline-secondary btn-sm" onClick={() => openLogDetail({ id: logDetail.id })}>Actualizar</button>
                <button className="btn btn-secondary btn-sm" onClick={() => setLogDetail(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Previsualización de la landing (iframe con el HTML real; assets desde inter-conecta.com vía <base>) */}
      {landingPreview && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} onClick={(e) => { if (e.target === e.currentTarget) setLandingPreview(null); }}>
          <div className="modal-dialog modal-xl modal-dialog-centered">
            <div className="modal-content" style={{ height: '90vh' }}>
              <div className="modal-header py-2">
                <h6 className="modal-title d-flex align-items-center"><BsGlobe className="me-2" />Previsualización de landing — {landingPreview.proposal.titulo}</h6>
                <div className="ms-auto d-flex gap-2 align-items-center">
                  {landingPreview.proposal.landing_url && (
                    <a className="btn btn-sm btn-outline-primary" href={landingPreview.proposal.landing_url} target="_blank" rel="noopener noreferrer"><BsBoxArrowUpRight className="me-1" size={12} />Abrir publicada</a>
                  )}
                  <button className="btn-close" onClick={() => setLandingPreview(null)}></button>
                </div>
              </div>
              <div className="modal-body p-0" style={{ overflow: 'hidden' }}>
                <iframe title="Previsualización landing" srcDoc={landingPreview.html} style={{ width: '100%', height: '100%', border: 0 }} sandbox="allow-scripts allow-same-origin" />
              </div>
              <div className="modal-footer py-1">
                <span className="text-muted small me-auto">Vista previa. El formulario y los enlaces son reales de inter-conecta.com; no envíes datos desde aquí.</span>
                <button className="btn btn-secondary btn-sm" onClick={() => setLandingPreview(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Previsualización del EMAIL de la campaña (HTML real de la plantilla en iframe) */}
      {emailPreview && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.5)' }} onClick={(e) => { if (e.target === e.currentTarget) setEmailPreview(null); }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ height: '90vh' }}>
              <div className="modal-header py-2">
                <h6 className="modal-title d-flex align-items-center"><BsEnvelope className="me-2" />Email de la campaña — {emailPreview.proposal.titulo}</h6>
                <button className="btn-close" onClick={() => setEmailPreview(null)}></button>
              </div>
              <div className="modal-body p-0" style={{ overflow: 'hidden', background: '#eef1f6' }}>
                <iframe title="Previsualización email" srcDoc={emailPreview.html} style={{ width: '100%', height: '100%', border: 0 }} sandbox="" />
              </div>
              <div className="modal-footer py-1">
                <span className="text-muted small me-auto">Así lo verá el destinatario. Pide cambios por el chat de la propuesta («Pedir ajuste»).</span>
                <button className="btn btn-secondary btn-sm" onClick={() => setEmailPreview(null)}>Cerrar</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {discardModal && (
        <div className="modal fade show" style={{ display: 'block', background: 'rgba(0,0,0,0.45)' }} onClick={() => setDiscardModal(null)}>
          <div className="modal-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-content">
              <div className="modal-header py-2">
                <h6 className="modal-title">Descartar «{discardModal.proposal.titulo}»</h6>
                <button className="btn-close" onClick={() => setDiscardModal(null)}></button>
              </div>
              <div className="modal-body">
                <p className="small text-muted mb-2">¿Por qué la descartas? El agente lo tendrá en cuenta en las próximas propuestas. Descartar esta propuesta no descarta el sector: solo queda vetado si marcas «El sector no nos interesa».</p>
                <div className="d-flex flex-column gap-1 mb-2">
                  {DISCARD_REASONS.map((r) => (
                    <div className="form-check" key={r.key}>
                      <input className="form-check-input" type="radio" name="discardReason" id={`dr-${r.key}`} checked={discardModal.motivo === r.key} onChange={() => setDiscardModal((m) => ({ ...m, motivo: r.key }))} />
                      <label className="form-check-label small" htmlFor={`dr-${r.key}`}>{r.label}</label>
                    </div>
                  ))}
                </div>
                <textarea className="form-control form-control-sm" rows={2} placeholder="Detalle opcional (p. ej. «este sector ya lo trabajamos por otra vía», «prefiero campañas de IA»)…" value={discardModal.nota} onChange={(e) => setDiscardModal((m) => ({ ...m, nota: e.target.value }))} />
              </div>
              <div className="modal-footer py-2">
                <button className="btn btn-sm btn-outline-secondary" onClick={() => setDiscardModal(null)}>Cancelar</button>
                <button className="btn btn-sm btn-danger" onClick={() => submitDiscard(discardModal.proposal, discardModal.motivo || null, discardModal.nota || null)}>Descartar</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentesPage;
